import React, { useState, useEffect } from 'react';
import { LogOut, Home, Dumbbell, MessageSquare, MessageCircle, Settings, ChevronRight, Pin, Calendar, AlertCircle, Info, Megaphone, Plus, Edit2, Trash2, X, Users, FileText, Activity, AlertTriangle, Check, XCircle, Award, TrendingUp, Star, User, UserPlus, Eye, Download } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Logók - Akadémia: pajzs lófejjel, Egyesület: lovastornász
const AKADEMIA_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_equestrian-hub-20/artifacts/rlfzxxnx_IMG_0605.png";
const EGYESULET_LOGO_WHITE = "https://customer-assets-eiarnc6j.emergentagent.net/job_equestrian-hub-20/artifacts/vrc3omri_IMG_0569.jpeg";

// Státusz színek
const STATUS_COLORS = {
  ok: 'bg-green-500',
  pending: 'bg-yellow-500', 
  problem: 'bg-red-500',
  info: 'bg-blue-500',
  okText: 'text-green-600',
  pendingText: 'text-yellow-600',
  problemText: 'text-red-600',
  infoText: 'text-blue-600',
  okBg: 'bg-green-50 border-green-200',
  pendingBg: 'bg-yellow-50 border-yellow-200',
  problemBg: 'bg-red-50 border-red-200',
  infoBg: 'bg-blue-50 border-blue-200',
};

// Header komponens - lila-kék gradient
const AkademiaHeader = ({ user, onLogout }) => {
  return (
    <header className="bg-gradient-to-r from-purple-600 via-purple-500 to-blue-500 text-white px-4 py-3 sticky top-0 z-50 shadow-lg">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <img 
            src={AKADEMIA_LOGO} 
            alt="Gepárd Lovas Akadémia" 
            className="h-10 w-10 rounded-full object-cover bg-white p-1"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="hidden sm:block">
            <h1 className="font-bold text-lg leading-tight">Gepárd Lovas Akadémia</h1>
            <p className="text-purple-100 text-xs">Orosháza</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {user && (
            <span className="text-sm text-purple-100 hidden sm:inline">
              {user.name}
            </span>
          )}
          <button
            onClick={onLogout}
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-xl text-sm font-medium transition-all"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Kilépés</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Mobil navigáció - alsó menüsor
const MobileNav = ({ activeTab, setActiveTab, isAdmin, unreadChatCount = 0 }) => {
  // Admin/edző esetén pénzügyek (bérlések + fizetések) tab
  const baseItems = isAdmin ? [
    { id: 'home', icon: Home, label: 'Főoldal' },
    { id: 'horses', icon: Award, label: 'Lovak' },
    { id: 'finances', icon: FileText, label: 'Pénzügyek' },
    { id: 'chat', icon: MessageCircle, label: 'Chat', badge: unreadChatCount },
    { id: 'admin', icon: Settings, label: 'Admin' },
  ] : [
    { id: 'home', icon: Home, label: 'Főoldal' },
    { id: 'workouts', icon: Dumbbell, label: 'Edzések' },
    { id: 'badges', icon: Award, label: 'Jelvények' },
    { id: 'documents', icon: FileText, label: 'Iratok' },
    { id: 'chat', icon: MessageCircle, label: 'Chat', badge: unreadChatCount },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 z-50 md:hidden">
      <div className="flex justify-around items-center max-w-lg mx-auto overflow-x-auto">
        {baseItems.map((item) => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => setActiveTab(item.id)}
            className={`flex flex-col items-center p-2 rounded-xl transition-all min-w-[55px] relative ${
              activeTab === item.id
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            <item.icon size={20} />
            <span className="text-xs mt-1 font-medium">{item.label}</span>
            {item.badge > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
};

// Kártya komponens - lekerekített, árnyékos
const Card = ({ children, className = '', status = null, onClick = null, ...rest }) => {
  const statusBg = status ? STATUS_COLORS[`${status}Bg`] : 'bg-white';
  
  return (
    <div 
      className={`${statusBg} rounded-2xl shadow-sm border p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
      {...rest}
    >
      {children}
    </div>
  );
};

// Nagy gomb komponens
const BigButton = ({ children, onClick, variant = 'primary', disabled = false, className = '', type = 'button', ...props }) => {
  const variants = {
    primary: 'bg-gradient-to-r from-purple-600 to-blue-500 text-white hover:from-purple-700 hover:to-blue-600',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    success: 'bg-green-500 text-white hover:bg-green-600',
    danger: 'bg-red-500 text-white hover:bg-red-600',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-4 px-6 rounded-2xl font-semibold text-lg transition-all ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Státusz badge
const StatusBadge = ({ status, label }) => {
  const colors = {
    ok: 'bg-green-100 text-green-700 border-green-300',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    problem: 'bg-red-100 text-red-700 border-red-300',
    info: 'bg-blue-100 text-blue-700 border-blue-300',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${colors[status]}`}>
      {label}
    </span>
  );
};

// Bejelentkezési képernyő
const LoginScreen = ({ onLogin, error }) => {
  const [username, setUsername] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !identifier.trim()) return;
    
    setLoading(true);
    await onLogin(username.trim(), identifier.trim());
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-500 to-blue-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logók */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <img 
            src={EGYESULET_LOGO_WHITE} 
            alt="Gepárd Lovas Egyesület" 
            className="h-20 w-20 object-contain rounded-xl"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <img 
            src={AKADEMIA_LOGO} 
            alt="Gepárd Lovas Akadémia" 
            className="h-20 w-20 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* Cím */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Gepárd Lovas Akadémia</h1>
          <p className="text-purple-100">Belépés a rendszerbe</p>
        </div>

        {/* Bejelentkezési form */}
        <Card className="bg-white/95 backdrop-blur">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="akademia-login-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Felhasználónév
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="pl. akadémista01"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all text-lg text-gray-900"
                autoComplete="username"
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Azonosító
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                value={identifier}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 10) setIdentifier(val);
                }}
                placeholder="10 számjegyű azonosító"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all text-lg tracking-wider text-gray-900"
                autoComplete="off"
                data-testid="identifier-input"
              />
              <p className="text-xs text-gray-500 mt-1">{identifier.length}/10 számjegy</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <BigButton type="submit" disabled={loading || !username || identifier.length !== 10} data-testid="login-submit-btn">
              {loading ? 'Belépés...' : 'Belépés'}
            </BigButton>
          </form>
        </Card>

        <p className="text-center text-purple-100 text-sm mt-6">
          Gepárd Lovas Egyesület • Orosháza
        </p>
      </div>
    </div>
  );
};

// Főoldal - személyre szabott dashboard
const HomePage = ({ user, token }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await axios.get(`${API}/akademia/dashboard?token=${token}`);
        setDashboard(response.data);
      } catch (err) {
        console.error('Failed to load dashboard', err);
      } finally {
        setLoading(false);
      }
    };
    if (token) loadDashboard();
  }, [token]);

  if (loading) {
    return <Card><p className="text-center text-gray-500 py-8">Betöltés...</p></Card>;
  }

  const userData = dashboard?.user || user;
  const payments = dashboard?.payments || { pending_count: 0, total_due: 0, items: [] };
  const progress = dashboard?.progress || { current_points: 0, next_badge_at: 100, percentage: 0 };
  const nextTraining = dashboard?.next_training;
  const announcements = dashboard?.announcements || { unread_count: 0, recent: [] };
  const adminSummary = dashboard?.admin_summary;
  const rentals = dashboard?.rentals;
  const isAdminOrCoach = userData?.role === 'admin' || userData?.role === 'edzo';

  // Get deadline color class
  const getDeadlineColor = (status) => {
    switch (status) {
      case 'overdue': return 'border-red-500 bg-red-50';
      case 'approaching': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-green-500 bg-green-50';
    }
  };

  const getDeadlineTextColor = (status) => {
    switch (status) {
      case 'overdue': return 'text-red-600';
      case 'approaching': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-4" data-testid="homepage">
      {/* Üdvözlés */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <h2 className="text-2xl font-bold text-gray-800">
          Üdvözöljük, {userData?.name || 'Akadémista'}!
        </h2>
        <p className="text-gray-600 mt-1">
          {isAdminOrCoach 
            ? `Jó munkát kívánunk a mai napra!`
            : `Jó látni újra a Gepárd Lovas Akadémián!`
          }
        </p>
        {userData?.training_direction && (
          <p className="text-sm text-purple-600 mt-1">Képzési irány: {userData.training_direction}</p>
        )}
      </Card>

      {/* Admin/Edző összefoglaló */}
      {isAdminOrCoach && adminSummary && (
        <Card className="border-purple-200" data-testid="admin-summary">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Settings size={18} className="text-purple-600" />
            Gyors áttekintés
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-xs text-blue-600 font-medium">Felhasználók</p>
              <p className="text-xl font-bold text-blue-700">{adminSummary.total_users}</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-3">
              <p className="text-xs text-orange-600 font-medium">Mai edzések</p>
              <p className="text-xl font-bold text-orange-700">{adminSummary.todays_trainings}</p>
            </div>
            {adminSummary.pending_deletions > 0 && (
              <div className="bg-red-50 rounded-xl p-3">
                <p className="text-xs text-red-600 font-medium">Törlési kérelmek</p>
                <p className="text-xl font-bold text-red-700">{adminSummary.pending_deletions}</p>
              </div>
            )}
            {adminSummary.inactive_users > 0 && (
              <div className="bg-yellow-50 rounded-xl p-3">
                <p className="text-xs text-yellow-600 font-medium">Inaktív tagok</p>
                <p className="text-xl font-bold text-yellow-700">{adminSummary.inactive_users}</p>
              </div>
            )}
            {adminSummary.overdue_payments > 0 && (
              <div className="bg-red-50 rounded-xl p-3">
                <p className="text-xs text-red-600 font-medium">Lejárt befizetések</p>
                <p className="text-xl font-bold text-red-700">{adminSummary.overdue_payments}</p>
              </div>
            )}
            <div className="bg-purple-50 rounded-xl p-3">
              <p className="text-xs text-purple-600 font-medium">Függő befizetések</p>
              <p className="text-xl font-bold text-purple-700">{adminSummary.pending_payments}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Pont/Jelvény kártya - minden felhasználónak */}
      <Card data-testid="points-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Jelenlegi pontszám</p>
            <p className="text-3xl font-bold text-purple-600">{progress.current_points}</p>
          </div>
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
            {progress.next_badge ? (
              <span className="text-3xl">{progress.next_badge.icon}</span>
            ) : (
              <span className="text-2xl">🏆</span>
            )}
          </div>
        </div>
        
        {/* Progress bar és következő jelvény */}
        <div className="mt-4">
          {progress.all_badges_earned ? (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-3">
              <p className="text-sm font-medium text-yellow-800">🎉 Gratulálunk! Az összes jelvényt megszereztél!</p>
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">
                  Következő: <span className="font-medium">{progress.next_badge?.name || 'Jelvény'}</span>
                </span>
                <span className="font-medium">{progress.current_points}/{progress.next_badge_at}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all" 
                  style={{ width: `${progress.percentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Még {progress.next_badge_at - progress.current_points} pont a {progress.next_badge?.name || 'következő jelvényig'}
              </p>
            </>
          )}
        </div>

        {/* Meglévő jelvények */}
        {userData?.badges?.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-gray-500 mb-2">Megszerzett jelvények:</p>
            <div className="flex gap-2 flex-wrap">
              {userData.badges.map((badge, i) => (
                <span 
                  key={i} 
                  className="text-xl bg-purple-50 rounded-full w-10 h-10 flex items-center justify-center" 
                  title={badge.name}
                >
                  {badge.icon || '🎖️'}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Fizetési kötelezettségek - részletes lista */}
      <Card 
        className={payments.items?.length > 0 
          ? `border-l-4 ${getDeadlineColor(payments.items[0]?.deadline_status)}`
          : 'border-l-4 border-l-green-500'}
        data-testid="payments-card"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
            payments.pending_count > 0 
              ? (payments.items[0]?.deadline_status === 'overdue' ? 'bg-red-500' : 'bg-yellow-500')
              : 'bg-green-500'
          }`}>
            {payments.pending_count > 0 ? '!' : '✓'}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-800">Fizetési kötelezettségek</p>
            {payments.pending_count > 0 ? (
              <p className={`text-sm ${getDeadlineTextColor(payments.items[0]?.deadline_status)}`}>
                {payments.pending_count} db • Összesen: {payments.total_due.toLocaleString()} Ft
              </p>
            ) : (
              <p className="text-sm text-green-600">Nincs aktuális fizetési kötelezettség</p>
            )}
          </div>
        </div>

        {/* Részletes lista */}
        {payments.items?.length > 0 && (
          <div className="space-y-2 mt-3 pt-3 border-t">
            {payments.items.map((payment, idx) => (
              <div 
                key={idx} 
                className={`flex items-center justify-between p-2 rounded-lg ${
                  payment.deadline_status === 'overdue' ? 'bg-red-50' :
                  payment.deadline_status === 'approaching' ? 'bg-yellow-50' : 'bg-gray-50'
                }`}
              >
                <div>
                  <p className="font-medium text-gray-800 text-sm">{payment.name || 'Befizetés'}</p>
                  <p className={`text-xs ${getDeadlineTextColor(payment.deadline_status)}`}>
                    {payment.deadline_status === 'overdue' ? '⚠️ Lejárt!' :
                     payment.deadline_status === 'approaching' ? `⏰ ${payment.days_until_deadline} nap van hátra` :
                     payment.deadline ? `📅 Határidő: ${formatDate(payment.deadline)}` : ''}
                  </p>
                </div>
                <p className="font-bold text-gray-800">{payment.amount?.toLocaleString()} Ft</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Következő edzés */}
      <Card data-testid="next-training-card">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            nextTraining ? 'bg-blue-500 text-white' : 'bg-blue-100'
          }`}>
            <Dumbbell size={20} className={nextTraining ? 'text-white' : 'text-blue-600'} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-800">Következő edzés</p>
            {nextTraining ? (
              <p className="text-sm text-blue-600">
                {nextTraining.date} {nextTraining.time} • {nextTraining.name || nextTraining.type}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Nincs beütemezett edzés</p>
            )}
          </div>
          {nextTraining && <ChevronRight size={20} className="text-gray-400" />}
        </div>
      </Card>

      {/* Havi megjelenések */}
      <Card data-testid="appearances-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Havi megjelenések</p>
            <p className="text-2xl font-bold text-gray-800">
              {userData?.monthly_appearances || 0} / {userData?.required_appearances || 4}
            </p>
          </div>
          <StatusBadge 
            status={(userData?.monthly_appearances || 0) >= (userData?.required_appearances || 4) ? 'ok' : 'pending'} 
            label={(userData?.monthly_appearances || 0) >= (userData?.required_appearances || 4) ? 'Teljesítve' : 'Folyamatban'} 
          />
        </div>
        <div className="mt-3">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map(i => (
              <div 
                key={i} 
                className={`flex-1 h-2 rounded-full ${
                  i <= (userData?.monthly_appearances || 0) ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </Card>

      {/* Bérlő specifikus kártyák */}
      {userData?.is_renter && rentals && (
        <>
          {/* Aktív bérlések */}
          {rentals.active?.length > 0 && (
            <Card className="border-l-4 border-l-blue-500" data-testid="rentals-card">
              <h3 className="font-semibold text-gray-800 mb-3">🐴 Aktív bérlések</h3>
              <div className="space-y-2">
                {rentals.active.map((rental, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-blue-50 p-2 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{rental.horse_name}</p>
                      <p className="text-xs text-gray-500">
                        {rental.start_date} - {rental.end_date || 'Folyamatos'}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      rental.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {rental.payment_status === 'paid' ? 'Fizetve' : 'Fizetendő'}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Takarmány kérések */}
          {rentals.feed_requests?.length > 0 && (
            <Card className="border-l-4 border-l-orange-500">
              <h3 className="font-semibold text-gray-800 mb-3">🌾 Függő takarmány kérések</h3>
              <div className="space-y-2">
                {rentals.feed_requests.map((req, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-orange-50 p-2 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">{req.horse_name}</p>
                      <p className="text-xs text-gray-500">{req.feed_type} • {req.amount}</p>
                    </div>
                    <span className="text-xs text-orange-600">Folyamatban</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Fontos közlemények */}
      {announcements.recent?.length > 0 && (
        <Card className="border-l-4 border-l-purple-500" data-testid="announcements-card">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Megaphone size={18} className="text-purple-600" />
            Fontos közlemények
            {announcements.unread_count > 0 && (
              <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                {announcements.unread_count}
              </span>
            )}
          </h3>
          <div className="space-y-2">
            {announcements.recent.map((ann, idx) => (
              <div key={idx} className="bg-purple-50 p-2 rounded-lg">
                <p className="font-medium text-gray-800 text-sm">{ann.title}</p>
                <p className="text-xs text-gray-600 line-clamp-2">{ann.content}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// Üzenőfal oldal
const MessageBoardPage = ({ user, token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    is_pinned: false,
    event_date: ''
  });
  const [error, setError] = useState('');
  const isAdmin = user?.role === 'admin';

  const MESSAGE_TYPES = [
    { id: 'info', label: 'Információ', icon: Info, color: 'blue' },
    { id: 'announcement', label: 'Közlemény', icon: Megaphone, color: 'purple' },
    { id: 'event', label: 'Esemény', icon: Calendar, color: 'green' },
    { id: 'important', label: 'Fontos', icon: AlertCircle, color: 'red' }
  ];

  // Üzenetek betöltése
  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/akademia/messages?token=${token}`);
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [token]);

  // Új üzenet modal megnyitása
  const openNewMessage = () => {
    setEditingMessage(null);
    setFormData({
      title: '',
      content: '',
      type: 'info',
      is_pinned: false,
      event_date: ''
    });
    setError('');
    setShowModal(true);
  };

  // Szerkesztés modal megnyitása
  const openEditMessage = (message) => {
    setEditingMessage(message);
    setFormData({
      title: message.title,
      content: message.content,
      type: message.type || 'info',
      is_pinned: message.is_pinned || false,
      event_date: message.event_date || ''
    });
    setError('');
    setShowModal(true);
  };

  // Üzenet mentése
  const saveMessage = async () => {
    setError('');
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('A cím és tartalom megadása kötelező');
      return;
    }
    try {
      if (editingMessage) {
        await axios.put(`${API}/akademia/messages/${editingMessage.id}?token=${token}`, formData);
      } else {
        await axios.post(`${API}/akademia/messages?token=${token}`, formData);
      }
      setShowModal(false);
      loadMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Üzenet törlése
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt az üzenetet?')) return;
    try {
      await axios.delete(`${API}/akademia/messages/${messageId}?token=${token}`);
      loadMessages();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Típus adatok lekérése
  const getTypeInfo = (type) => MESSAGE_TYPES.find(t => t.id === type) || MESSAGE_TYPES[0];

  // Dátum formázás
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Relatív idő
  const getRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} perce`;
    if (diffHours < 24) return `${diffHours} órája`;
    if (diffDays < 7) return `${diffDays} napja`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Card>
    );
  }

  // Kitűzött és normál üzenetek szétválasztása
  const pinnedMessages = messages.filter(m => m.is_pinned);
  const normalMessages = messages.filter(m => !m.is_pinned);

  return (
    <div className="space-y-4" data-testid="message-board-page">
      {/* Header admin gombbal */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Üzenőfal</h2>
        {isAdmin && (
          <button
            onClick={openNewMessage}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium text-sm"
            data-testid="new-message-btn"
          >
            <Plus size={18} />
            Új közlemény
          </button>
        )}
      </div>

      {/* Kitűzött üzenetek */}
      {pinnedMessages.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-500 flex items-center gap-1">
            <Pin size={14} /> Kitűzött
          </p>
          {pinnedMessages.map(message => {
            const typeInfo = getTypeInfo(message.type);
            const TypeIcon = typeInfo.icon;
            return (
              <Card key={message.id} className={`border-l-4 border-l-${typeInfo.color}-500`} data-testid={`pinned-message-${message.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <TypeIcon size={16} className={`text-${typeInfo.color}-500`} />
                      <span className={`text-xs font-medium text-${typeInfo.color}-600 bg-${typeInfo.color}-50 px-2 py-0.5 rounded-full`}>
                        {typeInfo.label}
                      </span>
                      <Pin size={12} className="text-yellow-500" />
                    </div>
                    <h3 className="font-semibold text-gray-800">{message.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 whitespace-pre-wrap">{message.content}</p>
                    {message.event_date && (
                      <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(message.event_date)}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      {message.author_name} • {getRelativeTime(message.created_at)}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditMessage(message)}
                        className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-gray-100"
                        data-testid={`edit-message-${message.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteMessage(message.id)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Normál üzenetek */}
      {normalMessages.length > 0 ? (
        <div className="space-y-3">
          {pinnedMessages.length > 0 && (
            <p className="text-sm font-medium text-gray-500 mt-4">Közlemények</p>
          )}
          {normalMessages.map(message => {
            const typeInfo = getTypeInfo(message.type);
            const TypeIcon = typeInfo.icon;
            return (
              <Card key={message.id} data-testid={`message-${message.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <TypeIcon size={16} className={`text-${typeInfo.color}-500`} />
                      <span className={`text-xs font-medium text-${typeInfo.color}-600 bg-${typeInfo.color}-50 px-2 py-0.5 rounded-full`}>
                        {typeInfo.label}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-800">{message.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 whitespace-pre-wrap">{message.content}</p>
                    {message.event_date && (
                      <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(message.event_date)}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      {message.author_name} • {getRelativeTime(message.created_at)}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditMessage(message)}
                        className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-gray-100"
                        data-testid={`edit-message-${message.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteMessage(message.id)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : pinnedMessages.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Még nincsenek közlemények</p>
            {isAdmin && (
              <button
                onClick={openNewMessage}
                className="mt-4 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium text-sm"
              >
                Első közlemény létrehozása
              </button>
            )}
          </div>
        </Card>
      ) : null}

      {/* Üzenet szerkesztő/létrehozó modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="message-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingMessage ? 'Közlemény szerkesztése' : 'Új közlemény'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-sm mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Típus választó */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Típus</label>
                <div className="grid grid-cols-2 gap-2">
                  {MESSAGE_TYPES.map(type => {
                    const TypeIcon = type.icon;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: type.id })}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          formData.type === type.id
                            ? `border-${type.color}-500 bg-${type.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`type-${type.id}`}
                      >
                        <TypeIcon size={18} className={`text-${type.color}-500`} />
                        <span className="text-sm font-medium text-gray-700">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Cím */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cím</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="Közlemény címe"
                  data-testid="message-title-input"
                />
              </div>

              {/* Tartalom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tartalom</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900 min-h-[120px] resize-none"
                  placeholder="Közlemény szövege..."
                  data-testid="message-content-input"
                />
              </div>

              {/* Esemény dátum (opcionális) */}
              {formData.type === 'event' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Esemény dátuma</label>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    data-testid="message-event-date"
                  />
                </div>
              )}

              {/* Kitűzés */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  data-testid="message-pinned-checkbox"
                />
                <div className="flex items-center gap-2">
                  <Pin size={16} className="text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Kitűzés az elejére</span>
                </div>
              </label>

              {/* Gombok */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                  data-testid="message-cancel-btn"
                >
                  Mégse
                </button>
                <button
                  onClick={saveMessage}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium"
                  data-testid="message-save-btn"
                >
                  {editingMessage ? 'Mentés' : 'Létrehozás'}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Edzések oldal - 8. FELADAT
const TrainingsPage = ({ user, token }) => {
  const [view, setView] = useState('upcoming'); // upcoming or history
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'altalanos',
    date: '',
    time: '',
    duration_minutes: 60,
    location: 'Lovarda',
    description: '',
    max_participants: 10,
    points_value: 10,
    is_public: true
  });
  const [error, setError] = useState('');
  const isAdminOrCoach = user?.role === 'admin' || user?.role === 'edzo';

  const TRAINING_TYPES = [
    { id: 'altalanos', label: 'Általános', color: 'blue' },
    { id: 'dij', label: 'Díjlovaglás', color: 'purple' },
    { id: 'ugras', label: 'Díjugrás', color: 'orange' },
    { id: 'tereplovas', label: 'Tereplovaglás', color: 'green' },
    { id: 'elmelet', label: 'Elmélet', color: 'gray' }
  ];

  // Load trainings
  const loadTrainings = async () => {
    setLoading(true);
    try {
      const endpoint = isAdminOrCoach 
        ? `${API}/akademia/trainings/all?token=${token}`
        : `${API}/akademia/trainings?token=${token}&view=${view}`;
      const response = await axios.get(endpoint);
      setTrainings(response.data);
    } catch (err) {
      console.error('Failed to load trainings', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrainings();
  }, [token, view]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('hu-HU', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Get type info
  const getTypeInfo = (type) => TRAINING_TYPES.find(t => t.id === type) || TRAINING_TYPES[0];

  // Open new training modal
  const openNewTraining = () => {
    setEditingTraining(null);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFormData({
      name: '',
      type: 'altalanos',
      date: tomorrow.toISOString().split('T')[0],
      time: '16:00',
      duration_minutes: 60,
      location: 'Lovarda',
      description: '',
      max_participants: 10,
      points_value: 10,
      is_public: true
    });
    setError('');
    setShowModal(true);
  };

  // Open edit modal
  const openEditTraining = (training) => {
    setEditingTraining(training);
    setFormData({
      name: training.name || '',
      type: training.type || 'altalanos',
      date: training.date || '',
      time: training.time || '',
      duration_minutes: training.duration_minutes || 60,
      location: training.location || 'Lovarda',
      description: training.description || '',
      max_participants: training.max_participants || 10,
      points_value: training.points_value || 10,
      is_public: training.is_public !== false
    });
    setError('');
    setShowModal(true);
  };

  // Save training
  const saveTraining = async () => {
    setError('');
    if (!formData.name.trim() || !formData.date) {
      setError('Név és dátum megadása kötelező');
      return;
    }
    try {
      if (editingTraining) {
        await axios.put(`${API}/akademia/trainings/${editingTraining.id}?token=${token}`, formData);
      } else {
        await axios.post(`${API}/akademia/trainings?token=${token}`, formData);
      }
      setShowModal(false);
      loadTrainings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Delete training
  const deleteTraining = async (trainingId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt az edzést?')) return;
    try {
      await axios.delete(`${API}/akademia/trainings/${trainingId}?token=${token}`);
      loadTrainings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Check if date is in past
  const isPast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="trainings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Edzések</h2>
        {isAdminOrCoach && (
          <button
            onClick={openNewTraining}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium text-sm"
            data-testid="new-training-btn"
          >
            <Plus size={18} />
            Új edzés
          </button>
        )}
      </div>

      {/* View toggle for students */}
      {!isAdminOrCoach && (
        <div className="flex gap-2">
          <button
            onClick={() => setView('upcoming')}
            className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
              view === 'upcoming'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
            data-testid="view-upcoming"
          >
            Közelgő edzések
          </button>
          <button
            onClick={() => setView('history')}
            className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
              view === 'history'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
            data-testid="view-history"
          >
            Edzéstörténet
          </button>
        </div>
      )}

      {/* Trainings list */}
      {trainings.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <Dumbbell size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {view === 'upcoming' ? 'Nincs közelgő edzés' : 'Nincs edzéstörténet'}
            </p>
            {isAdminOrCoach && (
              <button
                onClick={openNewTraining}
                className="mt-4 px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium text-sm"
              >
                Első edzés létrehozása
              </button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {trainings.map(training => {
            const typeInfo = getTypeInfo(training.type);
            const past = isPast(training.date);
            return (
              <Card 
                key={training.id} 
                className={`${past ? 'opacity-75' : ''}`}
                data-testid={`training-${training.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Date badge */}
                  <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${
                    past ? 'bg-gray-100' : 'bg-purple-100'
                  }`}>
                    <span className={`text-xs font-medium ${past ? 'text-gray-500' : 'text-purple-600'}`}>
                      {new Date(training.date).toLocaleDateString('hu-HU', { weekday: 'short' })}
                    </span>
                    <span className={`text-lg font-bold ${past ? 'text-gray-600' : 'text-purple-700'}`}>
                      {new Date(training.date).getDate()}
                    </span>
                  </div>
                  
                  {/* Training info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-800">{training.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full bg-${typeInfo.color}-100 text-${typeInfo.color}-700`}>
                        {typeInfo.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{training.time}</span>
                      <span>•</span>
                      <span>{training.duration_minutes} perc</span>
                      <span>•</span>
                      <span>{training.location}</span>
                    </div>
                    {training.instructor && (
                      <p className="text-sm text-gray-500 mt-1">Edző: {training.instructor}</p>
                    )}
                    
                    {/* Attendance info for students */}
                    {!isAdminOrCoach && past && (
                      <div className={`mt-2 text-sm ${training.attended ? 'text-green-600' : 'text-gray-400'}`}>
                        {training.attended 
                          ? `✓ Részt vettél (+${training.attendance_points} pont)` 
                          : '○ Nem vettél részt'}
                      </div>
                    )}
                    
                    {/* Attendance count for admin */}
                    {isAdminOrCoach && training.attendance_count !== undefined && (
                      <p className="text-sm text-blue-600 mt-1">
                        Résztvevők: {training.attendance_count}/{training.max_participants || '∞'}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  {isAdminOrCoach && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditTraining(training)}
                        className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-gray-100"
                        data-testid={`edit-training-${training.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteTraining(training.id)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                        data-testid={`delete-training-${training.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Training Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="training-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {editingTraining ? 'Edzés szerkesztése' : 'Új edzés'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-sm mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Edzés neve</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="pl. Díjlovaglás alapok"
                  data-testid="training-name-input"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Típus</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                >
                  {TRAINING_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dátum</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    data-testid="training-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Időpont</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    data-testid="training-time-input"
                  />
                </div>
              </div>

              {/* Duration and Location */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Időtartam (perc)</label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    min="15"
                    step="15"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Helyszín</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    placeholder="Lovarda"
                  />
                </div>
              </div>

              {/* Max participants and Points */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max résztvevő</label>
                  <input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 10 })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pontérték</label>
                  <input
                    type="number"
                    value={formData.points_value}
                    onChange={(e) => setFormData({ ...formData, points_value: parseInt(e.target.value) || 10 })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    min="0"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leírás (opcionális)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900 min-h-[80px] resize-none"
                  placeholder="Edzés leírása..."
                />
              </div>

              {/* Public toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Nyilvános (minden akadémista látja)</span>
              </label>

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                  data-testid="training-cancel-btn"
                >
                  Mégse
                </button>
                <button
                  onClick={saveTraining}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium"
                  data-testid="training-save-btn"
                >
                  {editingTraining ? 'Mentés' : 'Létrehozás'}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Egyéni Edzésterv oldal - 9. FELADAT
const TrainingPlanPage = ({ user, token }) => {
  const [plan, setPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [modificationReason, setModificationReason] = useState('');
  const [formData, setFormData] = useState({
    title: 'Egyéni edzésterv',
    description: '',
    goals: [],
    focus_areas: [],
    notes: '',
    start_date: '',
    end_date: ''
  });
  const [newGoal, setNewGoal] = useState('');
  const [newFocusArea, setNewFocusArea] = useState('');
  const [error, setError] = useState('');
  const isAdminOrCoach = user?.role === 'admin' || user?.role === 'edzo';

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      if (isAdminOrCoach) {
        // Load all plans and users
        const [plansRes, usersRes] = await Promise.all([
          axios.get(`${API}/akademia/training-plans?token=${token}`),
          axios.get(`${API}/akademia/users?token=${token}`)
        ]);
        setAllPlans(plansRes.data);
        setUsers(usersRes.data.filter(u => u.role === 'akademista'));
      } else {
        // Load own plan
        const response = await axios.get(`${API}/akademia/training-plans/${user.id}?token=${token}`);
        setPlan(response.data);
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token, user]);

  // Status colors and labels
  const getStatusInfo = (status) => {
    switch (status) {
      case 'accepted':
        return { color: 'green', label: 'Elfogadva', bg: 'bg-green-100 text-green-700' };
      case 'modification_requested':
        return { color: 'orange', label: 'Módosítás kérve', bg: 'bg-orange-100 text-orange-700' };
      default:
        return { color: 'yellow', label: 'Jóváhagyásra vár', bg: 'bg-yellow-100 text-yellow-700' };
    }
  };

  // Open create modal for specific user
  const openCreateModal = (targetUser) => {
    setSelectedUser(targetUser);
    setFormData({
      title: `${targetUser.name} edzésterve`,
      description: '',
      goals: [],
      focus_areas: [],
      notes: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: ''
    });
    setError('');
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (planToEdit) => {
    setSelectedPlan(planToEdit);
    setFormData({
      title: planToEdit.title || '',
      description: planToEdit.description || '',
      goals: planToEdit.goals || [],
      focus_areas: planToEdit.focus_areas || [],
      notes: planToEdit.notes || '',
      start_date: planToEdit.start_date || '',
      end_date: planToEdit.end_date || ''
    });
    setError('');
    setShowModal(true);
  };

  // Save plan
  const savePlan = async () => {
    setError('');
    try {
      if (selectedPlan) {
        await axios.put(`${API}/akademia/training-plans/${selectedPlan.id}?token=${token}`, formData);
      } else {
        await axios.post(`${API}/akademia/training-plans?token=${token}`, {
          ...formData,
          user_id: selectedUser.id
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Accept plan
  const acceptPlan = async (planId) => {
    try {
      await axios.post(`${API}/akademia/training-plans/${planId}/accept?token=${token}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Request modification
  const requestModification = async () => {
    if (!modificationReason.trim()) {
      alert('Kérjük, add meg a módosítás okát');
      return;
    }
    try {
      await axios.post(`${API}/akademia/training-plans/${plan.id}/request-modification?token=${token}`, {
        reason: modificationReason
      });
      setShowModifyModal(false);
      setModificationReason('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // View history
  const viewHistory = async (planId) => {
    try {
      const response = await axios.get(`${API}/akademia/training-plans/${planId}/history?token=${token}`);
      setHistory(response.data);
      setShowHistoryModal(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Add goal
  const addGoal = () => {
    if (newGoal.trim()) {
      setFormData({ ...formData, goals: [...formData.goals, newGoal.trim()] });
      setNewGoal('');
    }
  };

  // Add focus area
  const addFocusArea = () => {
    if (newFocusArea.trim()) {
      setFormData({ ...formData, focus_areas: [...formData.focus_areas, newFocusArea.trim()] });
      setNewFocusArea('');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  // Format action
  const formatAction = (action) => {
    switch (action) {
      case 'created': return 'Létrehozva';
      case 'updated': return 'Módosítva';
      case 'accepted': return 'Elfogadva';
      case 'modification_requested': return 'Módosítás kérve';
      default: return action;
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Card>
    );
  }

  // Student view
  if (!isAdminOrCoach) {
    if (!plan) {
      return (
        <Card data-testid="no-plan">
          <div className="text-center py-8">
            <FileText size={48} className="mx-auto text-gray-300 mb-3" />
            <h3 className="font-semibold text-gray-800 mb-2">Nincs edzésterved</h3>
            <p className="text-gray-500">Az edződ hamarosan elkészíti az egyéni edzéstervedet.</p>
          </div>
        </Card>
      );
    }

    const statusInfo = getStatusInfo(plan.status);

    return (
      <div className="space-y-4" data-testid="training-plan-student">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Egyéni edzéstervem</h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bg}`}>
            {statusInfo.label}
          </span>
        </div>

        <Card className={`border-l-4 ${plan.status === 'accepted' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
          <h3 className="text-lg font-bold text-gray-800 mb-2">{plan.title}</h3>
          {plan.description && <p className="text-gray-600 mb-4">{plan.description}</p>}
          
          {/* Goals */}
          {plan.goals?.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Célok:</h4>
              <ul className="space-y-1">
                {plan.goals.map((goal, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600">
                    <span className="text-green-500 mt-1">✓</span>
                    {goal}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Focus areas */}
          {plan.focus_areas?.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Fókuszterületek:</h4>
              <div className="flex flex-wrap gap-2">
                {plan.focus_areas.map((area, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {plan.notes && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Megjegyzések:</h4>
              <p className="text-gray-600 whitespace-pre-wrap">{plan.notes}</p>
            </div>
          )}

          {/* Dates */}
          <div className="flex gap-4 text-sm text-gray-500 mb-4">
            {plan.start_date && <span>Kezdés: {formatDate(plan.start_date)}</span>}
            {plan.end_date && <span>Befejezés: {formatDate(plan.end_date)}</span>}
          </div>

          <p className="text-xs text-gray-400">
            Készítette: {plan.created_by_name} • Verzió: {plan.version}
          </p>

          {/* Modification reason if any */}
          {plan.status === 'modification_requested' && plan.modification_reason && (
            <div className="mt-4 p-3 bg-orange-50 rounded-xl">
              <p className="text-sm text-orange-700">
                <strong>Módosítási kérelem:</strong> {plan.modification_reason}
              </p>
            </div>
          )}
        </Card>

        {/* Action buttons for student */}
        {plan.status === 'pending' && (
          <div className="flex gap-2">
            <button
              onClick={() => acceptPlan(plan.id)}
              className="flex-1 py-3 px-4 rounded-xl bg-green-500 text-white font-medium"
              data-testid="accept-plan-btn"
            >
              ✓ Elfogadom
            </button>
            <button
              onClick={() => setShowModifyModal(true)}
              className="flex-1 py-3 px-4 rounded-xl bg-orange-500 text-white font-medium"
              data-testid="request-modify-btn"
            >
              Módosítást kérek
            </button>
          </div>
        )}

        <button
          onClick={() => viewHistory(plan.id)}
          className="w-full py-2 text-purple-600 font-medium text-sm"
          data-testid="view-history-btn"
        >
          Változástörténet megtekintése
        </button>

        {/* Modification request modal */}
        {showModifyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md" data-testid="modify-modal">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Módosítás kérése</h3>
              <textarea
                value={modificationReason}
                onChange={(e) => setModificationReason(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-orange-500 outline-none text-gray-900 min-h-[120px] resize-none"
                placeholder="Írd le, milyen módosításokat szeretnél..."
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowModifyModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                >
                  Mégse
                </button>
                <button
                  onClick={requestModification}
                  className="flex-1 py-3 px-4 rounded-xl bg-orange-500 text-white font-medium"
                >
                  Kérelem küldése
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* History modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto" data-testid="history-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Változástörténet</h3>
                <button onClick={() => setShowHistoryModal(false)} className="text-gray-400">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-3">
                {history.map((entry, idx) => (
                  <div key={idx} className="border-l-2 border-purple-200 pl-3">
                    <p className="font-medium text-gray-800">{formatAction(entry.action)}</p>
                    <p className="text-sm text-gray-500">
                      {entry.performed_by_name} • {formatDate(entry.created_at)}
                    </p>
                    {entry.changes?.reason && (
                      <p className="text-sm text-orange-600 mt-1">Ok: {entry.changes.reason}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Admin/Coach view
  return (
    <div className="space-y-4" data-testid="training-plan-admin">
      <h2 className="text-xl font-bold text-gray-800">Egyéni edzéstervek</h2>

      {/* Users without plans */}
      {users.filter(u => !allPlans.find(p => p.user_id === u.id)).length > 0 && (
        <Card className="border-l-4 border-l-blue-500">
          <h3 className="font-semibold text-gray-800 mb-3">Edzésterv nélküli akadémisták</h3>
          <div className="space-y-2">
            {users.filter(u => !allPlans.find(p => p.user_id === u.id)).map(u => (
              <div key={u.id} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                <span className="text-gray-800">{u.name}</span>
                <button
                  onClick={() => openCreateModal(u)}
                  className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm"
                  data-testid={`create-plan-${u.id}`}
                >
                  + Terv létrehozása
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Existing plans */}
      {allPlans.length > 0 ? (
        <div className="space-y-3">
          {allPlans.map(p => {
            const statusInfo = getStatusInfo(p.status);
            return (
              <Card 
                key={p.id} 
                className={`border-l-4 ${
                  p.status === 'accepted' ? 'border-l-green-500' : 
                  p.status === 'modification_requested' ? 'border-l-orange-500' : 'border-l-yellow-500'
                }`}
                data-testid={`plan-${p.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-800">{p.user_name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{p.title}</p>
                    {p.status === 'modification_requested' && p.modification_reason && (
                      <p className="text-sm text-orange-600 mt-1">
                        Módosítás oka: {p.modification_reason}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Verzió: {p.version}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(p)}
                      className="p-2 text-gray-400 hover:text-purple-600 rounded-lg hover:bg-gray-100"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => viewHistory(p.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                    >
                      <FileText size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="text-center py-8">
            <FileText size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Még nincsenek edzéstervek</p>
          </div>
        </Card>
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plan-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {selectedPlan ? 'Edzésterv szerkesztése' : `Edzésterv: ${selectedUser?.name}`}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400">
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-sm mb-4">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cím</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leírás</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900 min-h-[80px] resize-none"
                />
              </div>

              {/* Goals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Célok</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    placeholder="Új cél hozzáadása"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGoal())}
                  />
                  <button onClick={addGoal} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-xl">
                    +
                  </button>
                </div>
                <div className="space-y-1">
                  {formData.goals.map((goal, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-lg">
                      <span className="flex-1 text-sm text-gray-700">{goal}</span>
                      <button
                        onClick={() => setFormData({ ...formData, goals: formData.goals.filter((_, i) => i !== idx) })}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Focus areas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fókuszterületek</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newFocusArea}
                    onChange={(e) => setNewFocusArea(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                    placeholder="pl. Ülés, Léptető, Vágtaátmenetek"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFocusArea())}
                  />
                  <button onClick={addFocusArea} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-xl">
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.focus_areas.map((area, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {area}
                      <button
                        onClick={() => setFormData({ ...formData, focus_areas: formData.focus_areas.filter((_, i) => i !== idx) })}
                        className="text-purple-400 hover:text-purple-600"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Megjegyzések</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900 min-h-[60px] resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kezdés</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Befejezés</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                >
                  Mégse
                </button>
                <button
                  onClick={savePlan}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium"
                >
                  {selectedPlan ? 'Mentés' : 'Létrehozás'}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* History modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto" data-testid="history-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Változástörténet</h3>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              {history.map((entry, idx) => (
                <div key={idx} className="border-l-2 border-purple-200 pl-3">
                  <p className="font-medium text-gray-800">{formatAction(entry.action)}</p>
                  <p className="text-sm text-gray-500">
                    {entry.performed_by_name} • {formatDate(entry.created_at)}
                  </p>
                  {entry.changes?.reason && (
                    <p className="text-sm text-orange-600 mt-1">Ok: {entry.changes.reason}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Pontok és Jelvények oldal - 10. FELADAT
const PointsAndBadgesPage = ({ user, token }) => {
  const [activeTab, setActiveTab] = useState('my-progress');
  const [badges, setBadges] = useState([]);
  const [pointReasons, setPointReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [pointHistory, setPointHistory] = useState([]);
  const [currentUser, setCurrentUser] = useState(null); // Fresh user data from API
  const [loading, setLoading] = useState(true);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [badgeForm, setBadgeForm] = useState({ name: '', icon: '🏅', description: '', type: 'manual', points_required: '' });
  const [pointsForm, setPointsForm] = useState({ points: 10, reason_id: '', reason: '', comment: '' });
  const [error, setError] = useState('');
  const isAdminOrCoach = user?.role === 'admin' || user?.role === 'edzo';

  const loadData = async () => {
    setLoading(true);
    try {
      // Load fresh user data from /akademia/me
      const [badgesRes, reasonsRes, meRes] = await Promise.all([
        axios.get(`${API}/akademia/badges?token=${token}`),
        axios.get(`${API}/akademia/point-reasons`),
        axios.get(`${API}/akademia/me?token=${token}`)
      ]);
      setBadges(badgesRes.data);
      setPointReasons(reasonsRes.data);
      setCurrentUser(meRes.data); // Fresh user data with points and badges
      
      if (isAdminOrCoach) {
        const usersRes = await axios.get(`${API}/akademia/users?token=${token}`);
        setUsers(usersRes.data);
      }
      const historyRes = await axios.get(`${API}/akademia/users/${user.id}/points/history?token=${token}`);
      setPointHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token, user]);

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : '';

  const openPointsModal = (targetUser) => {
    setSelectedUser(targetUser);
    setPointsForm({ points: 10, reason_id: '', reason: '', comment: '' });
    setError('');
    setShowPointsModal(true);
  };

  const selectReason = (reasonId) => {
    const reason = pointReasons.find(r => r.id === reasonId);
    setPointsForm({ ...pointsForm, reason_id: reasonId, points: reason ? reason.points : pointsForm.points });
  };

  const addPoints = async () => {
    try {
      const response = await axios.post(`${API}/akademia/users/${selectedUser.id}/points?token=${token}`, pointsForm);
      if (response.data.new_badges?.length > 0) alert(`🎉 Új jelvény: ${response.data.new_badges.map(b => b.name).join(', ')}`);
      setShowPointsModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const openCreateBadge = () => {
    setSelectedBadge(null);
    setBadgeForm({ name: '', icon: '🏅', description: '', type: 'manual', points_required: '' });
    setShowBadgeModal(true);
  };

  const saveBadge = async () => {
    if (!badgeForm.name.trim()) { setError('Jelvény neve kötelező'); return; }
    try {
      await axios.post(`${API}/akademia/badges?token=${token}`, { ...badgeForm, points_required: badgeForm.type === 'automatic' ? parseInt(badgeForm.points_required) || 0 : null });
      setShowBadgeModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const openAwardModal = (badge) => { setSelectedBadge(badge); setSelectedUsers([]); setShowAwardModal(true); };

  const awardBadge = async () => {
    if (selectedUsers.length === 0) { alert('Válassz legalább egy személyt'); return; }
    try {
      const response = await axios.post(`${API}/akademia/badges/${selectedBadge.id}/award?token=${token}`, { user_ids: selectedUsers });
      alert(response.data.message);
      setShowAwardModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const toggleUserSelection = (userId) => setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);

  const getNextBadge = () => {
    // Use fresh currentUser data or fall back to user prop
    const userData = currentUser || user;
    const currentPoints = userData?.points || 0;
    const earnedBadgeIds = (userData?.badges || []).map(b => b.id);
    const automaticBadges = badges.filter(b => b.type === 'automatic' && b.points_required);
    for (const badge of automaticBadges.sort((a, b) => a.points_required - b.points_required)) {
      if (!earnedBadgeIds.includes(badge.id) && currentPoints < badge.points_required) return badge;
    }
    return null;
  };

  if (loading) return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;

  // Use fresh currentUser data for display
  const userData = currentUser || user;
  const nextBadge = getNextBadge();
  const currentPoints = userData?.points || 0;
  const userBadges = userData?.badges || [];
  const progress = nextBadge ? Math.min(100, (currentPoints / nextBadge.points_required) * 100) : 100;

  return (
    <div className="space-y-4" data-testid="points-badges-page">
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button data-testid="subtab-my-progress" onClick={() => setActiveTab('my-progress')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm ${activeTab === 'my-progress' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}><TrendingUp size={16} /> Fejlődésem</button>
        {isAdminOrCoach && (<>
          <button data-testid="subtab-points" onClick={() => setActiveTab('points')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm ${activeTab === 'points' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}><Star size={16} /> Pontok</button>
          <button data-testid="subtab-badges" onClick={() => setActiveTab('badges')} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm ${activeTab === 'badges' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}><Award size={16} /> Jelvények</button>
        </>)}
      </div>

      {activeTab === 'my-progress' && (
        <div className="space-y-4">
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between mb-4">
              <div><p className="text-sm text-gray-500">Jelenlegi pontszám</p><p className="text-4xl font-bold text-purple-600" data-testid="current-points">{currentPoints}</p></div>
              {nextBadge ? <div className="text-center" data-testid="next-badge"><span className="text-4xl">{nextBadge.icon}</span><p className="text-xs text-gray-500 mt-1">{nextBadge.name}</p></div> : <div className="text-center" data-testid="all-badges-earned"><span className="text-4xl">🏆</span><p className="text-xs text-green-600 mt-1">Mind megszerzett!</p></div>}
            </div>
            {nextBadge ? (<><div className="w-full bg-gray-200 rounded-full h-3 mb-2"><div className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full" style={{ width: `${progress}%` }} /></div><p className="text-sm text-gray-600">Még <strong>{nextBadge.points_required - currentPoints}</strong> pont a <strong>{nextBadge.name}</strong> jelvényig</p></>) : <div className="bg-green-100 text-green-700 rounded-xl p-3 text-center">🎉 Gratulálunk! Az összes pont alapú jelvényt megszereztéd!</div>}
          </Card>
          <Card>
            <h3 className="font-semibold text-gray-800 mb-3" data-testid="earned-badges-title">Megszerzett jelvények ({userBadges.length})</h3>
            {userBadges.length > 0 ? <div className="grid grid-cols-2 gap-3">{userBadges.map((badge, idx) => <div key={idx} className="bg-purple-50 rounded-xl p-3 text-center"><span className="text-3xl">{badge.icon}</span><p className="font-medium text-gray-800 mt-1">{badge.name}</p><p className="text-xs text-gray-500">{formatDate(badge.earned_at)}</p></div>)}</div> : <p className="text-gray-500 text-center py-4">Még nincsenek jelvényeid</p>}
          </Card>
          <Card>
            <h3 className="font-semibold text-gray-800 mb-3">Pont előzmények</h3>
            {pointHistory.length > 0 ? <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="point-history-list">{pointHistory.map((t, idx) => <div key={idx} className={`flex items-center justify-between p-2 rounded-lg ${t.points > 0 ? 'bg-green-50' : 'bg-red-50'}`}><div><p className="text-sm font-medium text-gray-800">{t.reason}</p><p className="text-xs text-gray-400">{formatDate(t.created_at)}</p></div><span className={`font-bold ${t.points > 0 ? 'text-green-600' : 'text-red-600'}`}>{t.points > 0 ? '+' : ''}{t.points}</span></div>)}</div> : <p className="text-gray-500 text-center py-4">Még nincs pont előzmény</p>}
          </Card>
        </div>
      )}

      {activeTab === 'points' && isAdminOrCoach && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-800">Pontok kezelése</h2>
          <div className="space-y-2" data-testid="points-user-list">{users.filter(u => u.role === 'akademista').map(u => <Card key={u.id} className="flex items-center gap-3" data-testid={`points-user-${u.id}`}><div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">{u.name?.charAt(0)}</div><div className="flex-1"><p className="font-medium text-gray-800">{u.name}</p><p className="text-sm text-purple-600 font-bold" data-testid={`points-value-${u.id}`}>{u.points || 0} pont</p></div><button data-testid={`add-points-${u.id}`} onClick={() => openPointsModal(u)} className="px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium">+ Pont</button></Card>)}</div>
        </div>
      )}

      {activeTab === 'badges' && isAdminOrCoach && (
        <div className="space-y-4">
          <div className="flex items-center justify-between"><h2 className="text-xl font-bold text-gray-800">Jelvények kezelése</h2><button data-testid="new-badge-btn" onClick={openCreateBadge} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"><Plus size={16} /> Új jelvény</button></div>
          <div className="grid grid-cols-2 gap-3" data-testid="badge-list">{badges.map(badge => <Card key={badge.id} className="text-center" data-testid={`badge-card-${badge.id}`}><span className="text-4xl">{badge.icon}</span><p className="font-medium text-gray-800 mt-2">{badge.name}</p><span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${badge.type === 'automatic' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{badge.type === 'automatic' ? `${badge.points_required} pt` : 'Manuális'}</span><button data-testid={`award-badge-${badge.id}`} onClick={() => openAwardModal(badge)} className="mt-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs">Kioszt</button></Card>)}</div>
        </div>
      )}

      {showPointsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="points-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-gray-800">Pont: {selectedUser.name}</h3><button onClick={() => setShowPointsModal(false)} className="text-gray-400"><X size={20} /></button></div>
            {error && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-xl text-sm mb-4">{error}</div>}
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Ok kiválasztása</label><div className="grid grid-cols-2 gap-2">{pointReasons.filter(r => r.category === 'positive').map(r => <button key={r.id} data-testid={`reason-${r.id}`} onClick={() => selectReason(r.id)} className={`p-2 rounded-xl text-left text-sm ${pointsForm.reason_id === r.id ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50'}`}><span>{r.label}</span><span className="text-green-600 ml-1">+{r.points}</span></button>)}</div><div className="grid grid-cols-2 gap-2 mt-2">{pointReasons.filter(r => r.category === 'negative').map(r => <button key={r.id} data-testid={`reason-${r.id}`} onClick={() => selectReason(r.id)} className={`p-2 rounded-xl text-left text-sm ${pointsForm.reason_id === r.id ? 'bg-red-100 border-2 border-red-500' : 'bg-gray-50'}`}><span>{r.label}</span><span className="text-red-600 ml-1">{r.points}</span></button>)}</div></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Egyedi pontszám</label><input data-testid="points-input" type="number" value={pointsForm.points} onChange={(e) => setPointsForm({ ...pointsForm, points: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Megjegyzés</label><input data-testid="points-comment-input" type="text" value={pointsForm.comment} onChange={(e) => setPointsForm({ ...pointsForm, comment: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" placeholder="Egyéb megjegyzés..." /></div>
              <div className="flex gap-2"><button data-testid="points-cancel-btn" onClick={() => setShowPointsModal(false)} className="flex-1 py-3 rounded-xl border border-gray-300 font-medium">Mégse</button><button data-testid="points-save-btn" onClick={addPoints} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium">{pointsForm.points >= 0 ? `+${pointsForm.points}` : pointsForm.points} pont</button></div>
            </div>
          </Card>
        </div>
      )}

      {showBadgeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="badge-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-gray-800">Új jelvény</h3><button onClick={() => setShowBadgeModal(false)} className="text-gray-400"><X size={20} /></button></div>
            {error && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-xl text-sm mb-4" data-testid="badge-modal-error">{error}</div>}
            <div className="space-y-4">
              <div className="flex gap-3"><div className="w-20"><label className="block text-sm font-medium text-gray-700 mb-1">Ikon</label><input data-testid="badge-icon-input" type="text" value={badgeForm.icon} onChange={(e) => setBadgeForm({ ...badgeForm, icon: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-center text-2xl" maxLength={2} /></div><div className="flex-1"><label className="block text-sm font-medium text-gray-700 mb-1">Név</label><input data-testid="badge-name-input" type="text" value={badgeForm.name} onChange={(e) => setBadgeForm({ ...badgeForm, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" /></div></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Leírás</label><input data-testid="badge-description-input" type="text" value={badgeForm.description} onChange={(e) => setBadgeForm({ ...badgeForm, description: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Típus</label><div className="flex gap-2"><button data-testid="badge-type-manual" onClick={() => setBadgeForm({ ...badgeForm, type: 'manual' })} className={`flex-1 py-2 rounded-xl text-sm ${badgeForm.type === 'manual' ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-100'}`}>Manuális</button><button data-testid="badge-type-automatic" onClick={() => setBadgeForm({ ...badgeForm, type: 'automatic' })} className={`flex-1 py-2 rounded-xl text-sm ${badgeForm.type === 'automatic' ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-100'}`}>Automatikus</button></div></div>
              {badgeForm.type === 'automatic' && <div><label className="block text-sm font-medium text-gray-700 mb-1">Pont határ</label><input data-testid="badge-points-required-input" type="number" value={badgeForm.points_required} onChange={(e) => setBadgeForm({ ...badgeForm, points_required: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" /></div>}
              <div className="flex gap-2"><button data-testid="badge-cancel-btn" onClick={() => setShowBadgeModal(false)} className="flex-1 py-3 rounded-xl border border-gray-300 font-medium">Mégse</button><button data-testid="badge-save-btn" onClick={saveBadge} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium">Mentés</button></div>
            </div>
          </Card>
        </div>
      )}

      {showAwardModal && selectedBadge && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="award-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-gray-800"><span className="text-2xl mr-2">{selectedBadge.icon}</span>{selectedBadge.name} kiosztása</h3><button onClick={() => setShowAwardModal(false)} className="text-gray-400"><X size={20} /></button></div>
            <p className="text-sm text-gray-500 mb-4">Válaszd ki, kinek szeretnéd adni:</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">{users.filter(u => u.role === 'akademista').map(u => { const hasBadge = u.badges?.some(b => b.id === selectedBadge.id); return <label key={u.id} data-testid={`award-user-${u.id}`} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${hasBadge ? 'bg-gray-100 opacity-50' : selectedUsers.includes(u.id) ? 'bg-purple-100' : 'bg-gray-50'}`}><input data-testid={`award-checkbox-${u.id}`} type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => !hasBadge && toggleUserSelection(u.id)} disabled={hasBadge} className="w-5 h-5" /><span className="font-medium text-gray-800">{u.name}</span>{hasBadge && <span className="text-xs text-gray-500 ml-auto">Már megvan</span>}</label>; })}</div>
            <div className="flex gap-2 mt-4"><button data-testid="award-cancel-btn" onClick={() => setShowAwardModal(false)} className="flex-1 py-3 rounded-xl border border-gray-300 font-medium">Mégse</button><button data-testid="award-confirm-btn" onClick={awardBadge} disabled={selectedUsers.length === 0} className="flex-1 py-3 rounded-xl bg-green-600 text-white font-medium disabled:opacity-50">Kioszt ({selectedUsers.length})</button></div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== 11. FELADAT: CHAT PAGE ====================
const ChatPage = ({ user, token }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/akademia/chat/conversations?token=${token}`);
      setConversations(response.data);
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/akademia/chat/conversations/${conversationId}/messages?token=${token}`);
      setMessages(response.data);
      // Scroll to bottom
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await axios.get(`${API}/akademia/chat/users?token=${token}`);
      setAvailableUsers(response.data);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadConversations();
      await loadAvailableUsers();
      setLoading(false);
    };
    init();
  }, [token]);

  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
      // Auto-refresh messages every 5 seconds when conversation is open
      const interval = setInterval(() => loadMessages(activeConversation.id), 5000);
      return () => clearInterval(interval);
    }
  }, [activeConversation]);

  const startNewConversation = async (userId) => {
    try {
      const response = await axios.post(`${API}/akademia/chat/conversations?token=${token}`, { user_id: userId });
      const conv = response.data.conversation;
      setActiveConversation(conv);
      setShowNewChat(false);
      await loadConversations();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || sending) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/akademia/chat/conversations/${activeConversation.id}/messages?token=${token}`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      await loadMessages(activeConversation.id);
      await loadConversations();
    } catch (err) {
      alert(err.response?.data?.detail || 'Üzenet küldése sikertelen');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
      edzo: { label: 'Edző', color: 'bg-blue-100 text-blue-700' },
      akademista: { label: 'Akadémista', color: 'bg-green-100 text-green-700' }
    };
    return badges[role] || badges.akademista;
  };

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </Card>
    );
  }

  // Conversation view
  if (activeConversation) {
    const otherUser = activeConversation.other_participants?.[0] || {};
    const roleBadge = getRoleBadge(otherUser.role);
    
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]" data-testid="chat-conversation">
        {/* Header */}
        <div className="bg-white rounded-t-2xl border-b p-3 flex items-center gap-3">
          <button 
            onClick={() => { setActiveConversation(null); loadConversations(); }}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="chat-back-btn"
          >
            <ChevronRight size={20} className="rotate-180 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
            {otherUser.name?.charAt(0) || '?'}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-800">{otherUser.name || 'Ismeretlen'}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadge.color}`}>{roleBadge.label}</span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.length === 0 ? (
            <p className="text-center text-gray-400 py-8">Még nincsenek üzenetek. Kezdd a beszélgetést!</p>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user.id;
              return (
                <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    isOwn 
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isOwn ? 'text-purple-200' : 'text-gray-400'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="bg-white border-t p-3 rounded-b-2xl">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Írj üzenetet..."
              className="flex-1 px-4 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
              data-testid="chat-message-input"
              maxLength={2000}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              data-testid="chat-send-btn"
            >
              {sending ? '...' : 'Küldés'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Conversation list view
  return (
    <div className="space-y-4" data-testid="chat-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Chat</h2>
        <button
          onClick={() => setShowNewChat(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"
          data-testid="new-chat-btn"
        >
          <Plus size={16} />
          Új beszélgetés
        </button>
      </div>

      {conversations.length === 0 ? (
        <Card className="text-center py-8">
          <MessageCircle size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Még nincsenek beszélgetéseid</p>
          <p className="text-sm text-gray-400 mt-1">Kezdj új beszélgetést az edzőiddel vagy az adminnal!</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => {
            const other = conv.other_participants?.[0] || {};
            const roleBadge = getRoleBadge(other.role);
            return (
              <Card
                key={conv.id}
                onClick={() => setActiveConversation(conv)}
                className="cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`conversation-${conv.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg">
                      {other.name?.charAt(0) || '?'}
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                        {conv.unread_count > 9 ? '9+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800 truncate">{other.name || 'Ismeretlen'}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${roleBadge.color}`}>{roleBadge.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conv.last_message_preview || 'Nincs üzenet'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-400">{formatTime(conv.last_message_at)}</p>
                    <ChevronRight size={16} className="text-gray-400 ml-auto mt-1" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[80vh] overflow-hidden" data-testid="new-chat-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Új beszélgetés</h3>
              <button onClick={() => setShowNewChat(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            {availableUsers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nincs elérhető felhasználó</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                <p className="text-sm text-gray-500 mb-2">Válassz, kivel szeretnél beszélgetni:</p>
                {availableUsers.map((u) => {
                  const roleBadge = getRoleBadge(u.role);
                  return (
                    <button
                      key={u.id}
                      onClick={() => startNewConversation(u.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-purple-50 transition-colors text-left"
                      data-testid={`start-chat-${u.id}`}
                    >
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                        {u.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{u.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadge.color}`}>{roleBadge.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== 12. FELADAT: LOVAK ÉS BOXOK PAGE ====================
// Lovak a Tag oldalról jönnek, itt csak megtekintés és Akadémia-specifikus adatok kezelése
const HorsesAndBoxesPage = ({ user, token }) => {
  const [activeTab, setActiveTab] = useState('horses');
  const [horses, setHorses] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHorseModal, setShowHorseModal] = useState(false);
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [expandedHorse, setExpandedHorse] = useState(null);
  // Akademia-specifikus adatok (status, health_notes, description) - a ló alapadatai (név, nem, szín stb.) a Tag-ból jönnek
  const [horseForm, setHorseForm] = useState({
    status: 'aktív', health_notes: '', description: ''
  });
  const [boxForm, setBoxForm] = useState({
    number: '', name: '', status: 'szabad', size: '', location: '', notes: ''
  });
  const isAdmin = user?.role === 'admin';

  const loadData = async () => {
    setLoading(true);
    try {
      const [horsesRes, boxesRes] = await Promise.all([
        axios.get(`${API}/akademia/horses?token=${token}`),
        axios.get(`${API}/akademia/boxes?token=${token}`)
      ]);
      setHorses(horsesRes.data);
      setBoxes(boxesRes.data);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token]);

  // Csak Akadémia-specifikus adatok mentése (status, health_notes, description)
  const handleSaveHorseData = async () => {
    try {
      await axios.put(`${API}/akademia/horses/${selectedHorse.id}?token=${token}`, horseForm);
      setShowHorseModal(false);
      setSelectedHorse(null);
      setHorseForm({ status: 'aktív', health_notes: '', description: '' });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleSaveBox = async () => {
    try {
      if (selectedBox) {
        await axios.put(`${API}/akademia/boxes/${selectedBox.id}?token=${token}`, boxForm);
      } else {
        await axios.post(`${API}/akademia/boxes?token=${token}`, boxForm);
      }
      setShowBoxModal(false);
      setSelectedBox(null);
      setBoxForm({ number: '', name: '', status: 'szabad', size: '', location: '', notes: '' });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleDeleteBox = async (id) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a boxot?')) return;
    try {
      await axios.delete(`${API}/akademia/boxes/${id}?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleAssignHorse = async (horseId) => {
    try {
      await axios.post(`${API}/akademia/boxes/${selectedBox.id}/assign?token=${token}`, { horse_id: horseId || null });
      setShowAssignModal(false);
      setSelectedBox(null);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const openEditHorse = (horse) => {
    setSelectedHorse(horse);
    setHorseForm({
      status: horse.akademia_status || 'aktív',
      health_notes: horse.health_notes || '',
      description: horse.description || ''
    });
    setShowHorseModal(true);
  };

  const openEditBox = (box) => {
    setSelectedBox(box);
    setBoxForm({
      number: box.number || '', name: box.name || '', status: box.status || 'szabad',
      size: box.size || '', location: box.location || '', notes: box.notes || ''
    });
    setShowBoxModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'aktív': 'bg-green-100 text-green-700',
      'pihenő': 'bg-blue-100 text-blue-700',
      'sérült': 'bg-red-100 text-red-700',
      'bérelhető': 'bg-purple-100 text-purple-700',
      'nem elérhető': 'bg-gray-100 text-gray-700',
      'szabad': 'bg-green-100 text-green-700',
      'foglalt': 'bg-orange-100 text-orange-700',
      'karbantartás alatt': 'bg-yellow-100 text-yellow-700',
      'fenntartott': 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;
  }

  return (
    <div className="space-y-4" data-testid="horses-boxes-page">
      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('horses')}
          className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
            activeTab === 'horses' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'
          }`}
          data-testid="horses-tab"
        >
          <span className="text-lg">🐴</span> Lovak ({horses.length})
        </button>
        <button
          onClick={() => setActiveTab('boxes')}
          className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
            activeTab === 'boxes' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'
          }`}
          data-testid="boxes-tab"
        >
          <Home size={18} /> Boxok ({boxes.length})
        </button>
      </div>

      {/* Horses Tab - lovak a Tag oldalról jönnek */}
      {activeTab === 'horses' && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
            <Info size={16} className="inline mr-2" />
            A lovakat a <strong>Tag oldalon</strong> lehet hozzáadni. Itt az Akadémia-specifikus adatokat (státusz, megjegyzések) kezelheted.
          </div>
          
          {horses.length === 0 ? (
            <Card className="text-center py-8">
              <span className="text-4xl mb-2">🐴</span>
              <p className="text-gray-500">Még nincsenek lovak a Tag rendszerben</p>
              <p className="text-sm text-gray-400 mt-1">Lovakat a Tag oldalon tudsz hozzáadni</p>
            </Card>
          ) : (
            horses.map((horse) => (
              <Card key={horse.id} className="overflow-hidden" data-testid={`horse-card-${horse.id}`}>
                <div className="flex gap-3">
                  {/* Horse Image */}
                  <div className="w-20 h-20 rounded-xl bg-purple-100 flex items-center justify-center overflow-hidden shrink-0">
                    <span className="text-3xl">🐴</span>
                  </div>
                  
                  {/* Horse Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800 truncate">{horse.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(horse.akademia_status || 'aktív')}`}>
                        {horse.akademia_status || 'aktív'}
                      </span>
                    </div>
                    {horse.color && <p className="text-sm text-gray-500">{horse.color} {horse.gender}</p>}
                    {horse.renter && (
                      <p className="text-sm text-purple-600 mt-1">
                        <User size={12} className="inline mr-1" />
                        Bérlő: {horse.renter.name}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setExpandedHorse(expandedHorse === horse.id ? null : horse.id)}
                      className="p-2 text-gray-400 hover:text-purple-600"
                    >
                      <ChevronRight size={18} className={`transition-transform ${expandedHorse === horse.id ? 'rotate-90' : ''}`} />
                    </button>
                    {isAdmin && (
                      <button onClick={() => openEditHorse(horse)} className="p-2 text-gray-400 hover:text-blue-600" title="Akadémia adatok szerkesztése">
                        <Edit2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedHorse === horse.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-sm">
                    {horse.gender && <div><span className="text-gray-500">Nem:</span> {horse.gender}</div>}
                    {horse.birth_year && <div><span className="text-gray-500">Születési év:</span> {horse.birth_year}</div>}
                    {horse.hun_id && <div><span className="text-gray-500">HUN ID:</span> {horse.hun_id}</div>}
                    {horse.owner_id && <div><span className="text-gray-500">Tulajdonos ID:</span> {horse.owner_id}</div>}
                    {horse.description && <div className="col-span-2"><span className="text-gray-500">Leírás:</span> {horse.description}</div>}
                    {horse.health_notes && <div className="col-span-2 text-orange-600"><span className="text-gray-500">Egészségügyi megjegyzés:</span> {horse.health_notes}</div>}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Boxes Tab */}
      {activeTab === 'boxes' && (
        <div className="space-y-3">
          {isAdmin && (
            <button
              onClick={() => { setSelectedBox(null); setBoxForm({ number: '', name: '', status: 'szabad', size: '', location: '', notes: '' }); setShowBoxModal(true); }}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              data-testid="add-box-btn"
            >
              <Plus size={18} /> Új box hozzáadása
            </button>
          )}
          
          {boxes.length === 0 ? (
            <Card className="text-center py-8">
              <Home size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">Még nincsenek boxok regisztrálva</p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {boxes.map((box) => (
                <Card key={box.id} className="relative" data-testid={`box-card-${box.id}`}>
                  <div className="text-center">
                    <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center text-2xl mb-2 ${
                      box.horse ? 'bg-orange-100' : 'bg-green-100'
                    }`}>
                      {box.horse ? '🐴' : '🏠'}
                    </div>
                    <p className="font-bold text-gray-800">Box {box.number}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(box.status)}`}>
                      {box.status}
                    </span>
                    {box.horse && (
                      <p className="text-sm text-purple-600 mt-1 truncate">{box.horse.name}</p>
                    )}
                    {box.location && <p className="text-xs text-gray-400 mt-1">{box.location}</p>}
                  </div>
                  
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button onClick={() => { setSelectedBox(box); setShowAssignModal(true); }} className="p-1 text-gray-400 hover:text-purple-600" title="Ló hozzárendelése">
                        <UserPlus size={14} />
                      </button>
                      <button onClick={() => openEditBox(box)} className="p-1 text-gray-400 hover:text-blue-600">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDeleteBox(box.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Horse Akademia Data Modal - csak státusz, megjegyzések szerkesztése */}
      {showHorseModal && selectedHorse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-2">{selectedHorse.name} - Akadémia adatok</h3>
            <p className="text-sm text-gray-500 mb-4">Az alapadatok (név, nem, szín stb.) a Tag oldalon módosíthatók.</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Akadémia státusz</label>
                <select value={horseForm.status} onChange={(e) => setHorseForm({...horseForm, status: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900">
                  <option value="aktív">Aktív</option>
                  <option value="pihenő">Pihenő</option>
                  <option value="sérült">Sérült</option>
                  <option value="bérelhető">Bérelhető</option>
                  <option value="nem elérhető">Nem elérhető</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Leírás (Akadémia)</label>
                <textarea value={horseForm.description} onChange={(e) => setHorseForm({...horseForm, description: e.target.value})} placeholder="Leírás az Akadémia számára..." rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Egészségügyi megjegyzések</label>
                <textarea value={horseForm.health_notes} onChange={(e) => setHorseForm({...horseForm, health_notes: e.target.value})} placeholder="Egészségügyi megjegyzések..." rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowHorseModal(false)} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">Mégse</button>
              <button onClick={handleSaveHorseData} className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium" data-testid="save-horse-btn">Mentés</button>
            </div>
          </Card>
        </div>
      )}

      {/* Box Modal */}
      {showBoxModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{selectedBox ? 'Box szerkesztése' : 'Új box'}</h3>
            <div className="space-y-3">
              <input type="text" value={boxForm.number} onChange={(e) => setBoxForm({...boxForm, number: e.target.value})} placeholder="Box szám *" className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" data-testid="box-number-input" />
              <input type="text" value={boxForm.name} onChange={(e) => setBoxForm({...boxForm, name: e.target.value})} placeholder="Box neve" className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
              <select value={boxForm.status} onChange={(e) => setBoxForm({...boxForm, status: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900">
                <option value="szabad">Szabad</option>
                <option value="foglalt">Foglalt</option>
                <option value="karbantartás alatt">Karbantartás alatt</option>
                <option value="fenntartott">Fenntartott</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input type="text" value={boxForm.size} onChange={(e) => setBoxForm({...boxForm, size: e.target.value})} placeholder="Méret" className="px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
                <input type="text" value={boxForm.location} onChange={(e) => setBoxForm({...boxForm, location: e.target.value})} placeholder="Elhelyezkedés" className="px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
              </div>
              <textarea value={boxForm.notes} onChange={(e) => setBoxForm({...boxForm, notes: e.target.value})} placeholder="Megjegyzések" rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900" />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowBoxModal(false)} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">Mégse</button>
              <button onClick={handleSaveBox} disabled={!boxForm.number} className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50" data-testid="save-box-btn">Mentés</button>
            </div>
          </Card>
        </div>
      )}

      {/* Assign Horse Modal */}
      {showAssignModal && selectedBox && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Ló hozzárendelése - Box {selectedBox.number}</h3>
            <div className="space-y-2">
              <button
                onClick={() => handleAssignHorse(null)}
                className="w-full p-3 rounded-xl border border-gray-200 text-left hover:bg-red-50 flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">❌</div>
                <span className="text-gray-600">Ló eltávolítása a boxból</span>
              </button>
              {horses.map((horse) => (
                <button
                  key={horse.id}
                  onClick={() => handleAssignHorse(horse.id)}
                  className={`w-full p-3 rounded-xl border text-left hover:bg-purple-50 flex items-center gap-3 ${
                    horse.box_id === selectedBox.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-xl">🐴</div>
                  <div>
                    <p className="font-medium text-gray-800">{horse.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(horse.akademia_status || 'aktív')}`}>{horse.akademia_status || 'aktív'}</span>
                  </div>
                  {horse.box_id === selectedBox.id && <Check size={18} className="ml-auto text-purple-600" />}
                </button>
              ))}
            </div>
            <button onClick={() => setShowAssignModal(false)} className="w-full py-2 mt-4 border border-gray-300 rounded-xl text-gray-600">Bezárás</button>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== 13. FELADAT: LÓBÉRLÉSEK PAGE ====================
const RentalsPage = ({ user, token }) => {
  const [rentals, setRentals] = useState([]);
  const [horses, setHorses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [terminateReason, setTerminateReason] = useState('');
  const [form, setForm] = useState({
    student_id: '', horse_id: '', rental_type: 'teljes bérlés', start_date: '',
    monthly_fee: '', weekly_rides: '', rented_days: [], notes: '', contract_url: ''
  });
  const isAdmin = user?.role === 'admin';
  const isTrainer = user?.role === 'edzo';
  const canManage = isAdmin || isTrainer;

  const DAYS = [
    { id: 'monday', label: 'Hétfő' },
    { id: 'tuesday', label: 'Kedd' },
    { id: 'wednesday', label: 'Szerda' },
    { id: 'thursday', label: 'Csütörtök' },
    { id: 'friday', label: 'Péntek' },
    { id: 'saturday', label: 'Szombat' },
    { id: 'sunday', label: 'Vasárnap' }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const rentalsRes = await axios.get(`${API}/akademia/rentals?token=${token}`);
      setRentals(rentalsRes.data);
      
      if (canManage) {
        const [horsesRes, usersRes] = await Promise.all([
          axios.get(`${API}/akademia/horses?token=${token}`),
          axios.get(`${API}/akademia/users?token=${token}`)
        ]);
        setHorses(horsesRes.data);
        setStudents(usersRes.data.filter(u => u.role === 'akademista'));
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token]);

  const handleSave = async () => {
    try {
      if (selectedRental) {
        await axios.put(`${API}/akademia/rentals/${selectedRental.id}?token=${token}`, form);
      } else {
        await axios.post(`${API}/akademia/rentals?token=${token}`, form);
      }
      setShowModal(false);
      setSelectedRental(null);
      resetForm();
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleTerminate = async () => {
    try {
      const result = await axios.post(`${API}/akademia/rentals/${selectedRental.id}/terminate?token=${token}`, {
        reason: terminateReason
      });
      if (result.data.early_termination) {
        alert('Figyelem: A bérlés a minimum 3 hónapos időszak előtt lett felmondva.');
      }
      setShowTerminateModal(false);
      setSelectedRental(null);
      setTerminateReason('');
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const resetForm = () => {
    setForm({
      student_id: '', horse_id: '', rental_type: 'teljes bérlés', start_date: '',
      monthly_fee: '', weekly_rides: '', rented_days: [], notes: '', contract_url: ''
    });
  };

  const openEdit = (rental) => {
    setSelectedRental(rental);
    setForm({
      student_id: rental.student_id || '',
      horse_id: rental.horse_id || '',
      rental_type: rental.rental_type || 'teljes bérlés',
      start_date: rental.start_date?.split('T')[0] || '',
      monthly_fee: rental.monthly_fee || '',
      weekly_rides: rental.weekly_rides || '',
      rented_days: rental.rented_days || [],
      notes: rental.notes || '',
      contract_url: rental.contract_url || '',
      status: rental.status || 'aktív'
    });
    setShowModal(true);
  };

  const toggleDay = (dayId) => {
    setForm(prev => ({
      ...prev,
      rented_days: prev.rented_days.includes(dayId)
        ? prev.rented_days.filter(d => d !== dayId)
        : [...prev.rented_days, dayId]
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'aktív': 'bg-green-100 text-green-700',
      'függőben': 'bg-yellow-100 text-yellow-700',
      'lejárt': 'bg-gray-100 text-gray-700',
      'megszűnt': 'bg-red-100 text-red-700',
      'felfüggesztett': 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getRentalTypeIcon = (type) => {
    const icons = { 'teljes bérlés': '🐴', 'félbérlés': '🐎', 'negyedbérlés': '🏇' };
    return icons[type] || '🐴';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;
  }

  return (
    <div className="space-y-4" data-testid="rentals-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Lóbérlések</h2>
        {canManage && (
          <button
            onClick={() => { setSelectedRental(null); resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"
            data-testid="new-rental-btn"
          >
            <Plus size={16} /> Új bérlés
          </button>
        )}
      </div>

      {rentals.length === 0 ? (
        <Card className="text-center py-8">
          <span className="text-4xl mb-2">📋</span>
          <p className="text-gray-500">Még nincsenek lóbérlések</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {rentals.map((rental) => (
            <Card key={rental.id} data-testid={`rental-card-${rental.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-2xl shrink-0">
                  {getRentalTypeIcon(rental.rental_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800">{rental.horse?.name || 'Ismeretlen ló'}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(rental.status)}`}>
                      {rental.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <User size={12} className="inline mr-1" />
                    {rental.student?.name || 'Ismeretlen tanuló'}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                    <span>{rental.rental_type}</span>
                    <span>•</span>
                    <span>{formatCurrency(rental.monthly_fee)}/hó</span>
                    {rental.weekly_rides > 0 && (
                      <>
                        <span>•</span>
                        <span>{rental.weekly_rides} lovaglás/hét</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Kezdés: {formatDate(rental.start_date)}
                    {rental.end_date && ` • Vége: ${formatDate(rental.end_date)}`}
                  </p>
                </div>
                {canManage && rental.status === 'aktív' && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(rental)} className="p-2 text-gray-400 hover:text-blue-600">
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={() => { setSelectedRental(rental); setShowTerminateModal(true); }}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Felmondás"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                )}
              </div>
              {rental.rented_days?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Napok: {rental.rented_days.map(d => DAYS.find(day => day.id === d)?.label).filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {selectedRental ? 'Bérlés szerkesztése' : 'Új lóbérlés'}
            </h3>
            <div className="space-y-3">
              {!selectedRental && (
                <>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Akadémista *</label>
                    <select
                      value={form.student_id}
                      onChange={(e) => setForm({...form, student_id: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                      data-testid="rental-student-select"
                    >
                      <option value="">Válassz akadémistát...</option>
                      {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Ló *</label>
                    <select
                      value={form.horse_id}
                      onChange={(e) => setForm({...form, horse_id: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                      data-testid="rental-horse-select"
                    >
                      <option value="">Válassz lovat...</option>
                      {horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                  </div>
                </>
              )}
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Bérlés típusa *</label>
                <select
                  value={form.rental_type}
                  onChange={(e) => setForm({...form, rental_type: e.target.value})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                >
                  <option value="teljes bérlés">Teljes bérlés</option>
                  <option value="félbérlés">Félbérlés</option>
                  <option value="negyedbérlés">Negyedbérlés</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Kezdő dátum *</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({...form, start_date: e.target.value})}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Havi díj (Ft) *</label>
                  <input
                    type="number"
                    value={form.monthly_fee}
                    onChange={(e) => setForm({...form, monthly_fee: e.target.value})}
                    placeholder="pl. 50000"
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Heti lovaglási lehetőségek</label>
                <input
                  type="number"
                  value={form.weekly_rides}
                  onChange={(e) => setForm({...form, weekly_rides: e.target.value})}
                  placeholder="pl. 3"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Bérelt napok</label>
                <div className="flex flex-wrap gap-1">
                  {DAYS.map(day => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleDay(day.id)}
                      className={`px-2 py-1 text-xs rounded-lg ${
                        form.rented_days.includes(day.id)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {selectedRental && (
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Státusz</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({...form, status: e.target.value})}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  >
                    <option value="aktív">Aktív</option>
                    <option value="függőben">Függőben</option>
                    <option value="felfüggesztett">Felfüggesztett</option>
                  </select>
                </div>
              )}
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Szerződés URL</label>
                <input
                  type="text"
                  value={form.contract_url}
                  onChange={(e) => setForm({...form, contract_url: e.target.value})}
                  placeholder="Szerződés link..."
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megjegyzések</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  placeholder="Megjegyzések..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              {!selectedRental && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-700">
                  <AlertTriangle size={16} className="inline mr-1" />
                  A minimum bérlési idő <strong>3 hónap</strong>. Korai felmondás csak indoklással lehetséges.
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowModal(false); setSelectedRental(null); }} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">
                Mégse
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedRental && (!form.student_id || !form.horse_id || !form.start_date || !form.monthly_fee)}
                className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
                data-testid="save-rental-btn"
              >
                Mentés
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Terminate Modal */}
      {showTerminateModal && selectedRental && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Bérlés felmondása</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{selectedRental.horse?.name}</strong> bérlésének felmondása 
              ({selectedRental.student?.name})
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 mb-4">
              <AlertTriangle size={16} className="inline mr-1" />
              Figyelem: A minimum bérlési idő 3 hónap. Ha ennél korábban mondod fel, kötelező az indoklás megadása!
            </div>
            
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Felmondás indoklása</label>
              <textarea
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                placeholder="Indoklás (korai felmondás esetén kötelező)..."
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
              />
            </div>
            
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowTerminateModal(false); setSelectedRental(null); setTerminateReason(''); }} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">
                Mégse
              </button>
              <button
                onClick={handleTerminate}
                className="flex-1 py-2 bg-red-600 text-white rounded-xl font-medium"
              >
                Felmondás
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== 14. FELADAT: FIZETÉSEK PAGE ====================
const PaymentsPage = ({ user, token }) => {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({
    student_id: '', name: '', payment_type: 'egyéb díj', amount: '', due_date: '', notes: ''
  });
  const [payForm, setPayForm] = useState({ amount: '', method: 'készpénz', notes: '' });
  const isAdmin = user?.role === 'admin';

  const PAYMENT_TYPES = ['bérleti díj', 'edzésdíj', 'tandíj', 'egyéb díj', 'felszerelés', 'versenydíj'];
  const PAYMENT_METHODS = ['készpénz', 'átutalás', 'kártya', 'egyéb'];

  const loadData = async () => {
    setLoading(true);
    try {
      const url = filterStatus 
        ? `${API}/akademia/payments?token=${token}&status=${filterStatus}`
        : `${API}/akademia/payments?token=${token}`;
      const [paymentsRes, summaryRes, usersRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/akademia/payments/summary?token=${token}`),
        axios.get(`${API}/akademia/users?token=${token}`)
      ]);
      setPayments(paymentsRes.data);
      setSummary(summaryRes.data);
      setStudents(usersRes.data.filter(u => u.role === 'akademista'));
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token, filterStatus]);

  const handleSave = async () => {
    try {
      // Ensure amount is sent as a number
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0
      };
      if (selectedPayment) {
        await axios.put(`${API}/akademia/payments/${selectedPayment.id}?token=${token}`, payload);
      } else {
        await axios.post(`${API}/akademia/payments?token=${token}`, payload);
      }
      setShowModal(false);
      setSelectedPayment(null);
      resetForm();
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handlePay = async () => {
    try {
      await axios.post(`${API}/akademia/payments/${selectedPayment.id}/pay?token=${token}`, {
        amount: parseFloat(payForm.amount),
        method: payForm.method,
        notes: payForm.notes
      });
      setShowPayModal(false);
      setSelectedPayment(null);
      setPayForm({ amount: '', method: 'készpénz', notes: '' });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a fizetési kötelezettséget?')) return;
    try {
      await axios.delete(`${API}/akademia/payments/${id}?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const resetForm = () => {
    setForm({ student_id: '', name: '', payment_type: 'egyéb díj', amount: '', due_date: '', notes: '' });
  };

  const openEdit = (payment) => {
    setSelectedPayment(payment);
    setForm({
      student_id: payment.student_id || '',
      name: payment.name || '',
      payment_type: payment.payment_type || 'egyéb díj',
      amount: payment.amount || '',
      due_date: payment.due_date?.split('T')[0] || '',
      notes: payment.notes || ''
    });
    setShowModal(true);
  };

  const openPay = (payment) => {
    setSelectedPayment(payment);
    setPayForm({ amount: payment.amount - (payment.amount_paid || 0), method: 'készpénz', notes: '' });
    setShowPayModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'fizetésre vár': 'bg-yellow-100 text-yellow-700',
      'lejárt': 'bg-red-100 text-red-700',
      'fizetve': 'bg-green-100 text-green-700',
      'részben fizetve': 'bg-orange-100 text-orange-700',
      'törölve': 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getDueDateColor = (dueDate, status) => {
    if (status === 'fizetve') return 'text-green-600';
    if (!dueDate) return 'text-gray-500';
    
    const due = new Date(dueDate);
    const now = new Date();
    const daysUntil = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return 'text-red-600 font-bold';
    if (daysUntil <= 5) return 'text-orange-600 font-semibold';
    return 'text-gray-600';
  };

  if (loading) {
    return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;
  }

  return (
    <div className="space-y-4" data-testid="payments-page">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-yellow-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-yellow-700">{summary.counts.awaiting}</p>
            <p className="text-xs text-yellow-600">Fizetésre vár</p>
          </div>
          <div className="bg-red-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{summary.counts.overdue}</p>
            <p className="text-xs text-red-600">Lejárt</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.amounts.paid)}</p>
            <p className="text-xs text-green-600">Befizetett</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.amounts.outstanding)}</p>
            <p className="text-xs text-purple-600">Kintlévőség</p>
          </div>
        </div>
      )}

      {/* Header with filter */}
      <div className="flex items-center justify-between gap-2">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-300 text-gray-900 text-sm"
        >
          <option value="">Minden státusz</option>
          <option value="fizetésre vár">Fizetésre vár</option>
          <option value="lejárt">Lejárt</option>
          <option value="fizetve">Fizetve</option>
          <option value="részben fizetve">Részben fizetve</option>
        </select>
        <button
          onClick={() => { setSelectedPayment(null); resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"
          data-testid="new-payment-btn"
        >
          <Plus size={16} /> Új fizetés
        </button>
      </div>

      {/* Payments List */}
      {payments.length === 0 ? (
        <Card className="text-center py-8">
          <span className="text-4xl mb-2">💰</span>
          <p className="text-gray-500">Nincs fizetési kötelezettség</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {payments.map((payment) => (
            <Card key={payment.id} data-testid={`payment-card-${payment.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                  payment.status === 'fizetve' ? 'bg-green-100' : 
                  payment.status === 'lejárt' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {payment.status === 'fizetve' ? '✓' : payment.status === 'lejárt' ? '!' : '💳'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800">{payment.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <User size={12} className="inline mr-1" />
                    {payment.student?.name || 'Ismeretlen'}
                  </p>
                  <div className="flex items-center gap-2 text-sm mt-1">
                    <span className="font-semibold text-gray-800">{formatCurrency(payment.amount)}</span>
                    {payment.amount_paid > 0 && payment.status !== 'fizetve' && (
                      <span className="text-green-600">({formatCurrency(payment.amount_paid)} fizetve)</span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${getDueDateColor(payment.due_date, payment.status)}`}>
                    <Calendar size={12} className="inline mr-1" />
                    Határidő: {formatDate(payment.due_date)}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {payment.status !== 'fizetve' && payment.status !== 'törölve' && (
                    <button onClick={() => openPay(payment)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Fizetés rögzítése">
                      <Check size={16} />
                    </button>
                  )}
                  <button onClick={() => openEdit(payment)} className="p-2 text-gray-400 hover:text-blue-600">
                    <Edit2 size={14} />
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(payment.id)} className="p-2 text-gray-400 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {selectedPayment ? 'Fizetés szerkesztése' : 'Új fizetési kötelezettség'}
            </h3>
            <div className="space-y-3">
              {!selectedPayment && (
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Akadémista *</label>
                  <select
                    value={form.student_id}
                    onChange={(e) => setForm({...form, student_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  >
                    <option value="">Válassz akadémistát...</option>
                    {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megnevezés *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="pl. Januári bérleti díj"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Típus</label>
                <select
                  value={form.payment_type}
                  onChange={(e) => setForm({...form, payment_type: e.target.value})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                >
                  {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Összeg (Ft) *</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({...form, amount: e.target.value})}
                    placeholder="50000"
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Határidő *</label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({...form, due_date: e.target.value})}
                    className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megjegyzés</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  placeholder="Megjegyzések..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowModal(false); setSelectedPayment(null); }} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">
                Mégse
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedPayment && (!form.student_id || !form.name || !form.amount || !form.due_date)}
                className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Mentés
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Fizetés rögzítése</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{selectedPayment.name}</strong> ({selectedPayment.student?.name})
              <br />
              Összeg: {formatCurrency(selectedPayment.amount)} | 
              Már fizetve: {formatCurrency(selectedPayment.amount_paid || 0)}
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Fizetett összeg (Ft) *</label>
                <input
                  type="number"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({...payForm, amount: e.target.value})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Fizetési mód</label>
                <select
                  value={payForm.method}
                  onChange={(e) => setPayForm({...payForm, method: e.target.value})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                >
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megjegyzés</label>
                <input
                  type="text"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({...payForm, notes: e.target.value})}
                  placeholder="pl. Átutalás hivatkozási szám"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowPayModal(false); setSelectedPayment(null); }} className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600">
                Mégse
              </button>
              <button
                onClick={handlePay}
                disabled={!payForm.amount || parseFloat(payForm.amount) <= 0}
                className="flex-1 py-2 bg-green-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Fizetés rögzítése
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== 15. FELADAT: TÁPIGÉNYLÉS PAGE ====================
const FeedRequestsPage = ({ user, token }) => {
  const [feedRequests, setFeedRequests] = useState([]);
  const [activeRental, setActiveRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [form, setForm] = useState({ feed_type: '', quantity: '', notes: '' });
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  const isAdmin = user?.role === 'admin' || user?.role === 'edzo';
  
  const FEED_TYPES = ['Abrak', 'Széna', 'Müzli', 'Vitamin/Ásványi anyag', 'Egyéb'];

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsRes, rentalsRes] = await Promise.all([
        axios.get(`${API}/akademia/feed-requests?token=${token}`),
        isAdmin ? Promise.resolve({ data: [] }) : axios.get(`${API}/akademia/rentals?token=${token}`)
      ]);
      setFeedRequests(requestsRes.data);
      
      // Find active rental for current user (students only)
      if (!isAdmin && rentalsRes.data) {
        const active = rentalsRes.data.find(r => r.student_id === user?.id && r.status === 'aktív');
        setActiveRental(active);
      }
    } catch (err) {
      console.error('Failed to load feed requests', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token]);

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/akademia/feed-requests?token=${token}`, form);
      setShowModal(false);
      setForm({ feed_type: '', quantity: '', notes: '' });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleApproval = async () => {
    try {
      await axios.put(`${API}/akademia/feed-requests/${selectedRequest.id}/approve?token=${token}`, {
        action: approvalAction,
        notes: approvalNotes
      });
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setApprovalAction('');
      setApprovalNotes('');
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleFulfill = async (requestId) => {
    if (!window.confirm('Biztosan teljesítettnek jelölöd ezt az igénylést?')) return;
    try {
      await axios.put(`${API}/akademia/feed-requests/${requestId}/fulfill?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Biztosan törlöd ezt az igénylést?')) return;
    try {
      await axios.delete(`${API}/akademia/feed-requests/${requestId}?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'függőben': 'bg-yellow-100 text-yellow-700',
      'jóváhagyva': 'bg-blue-100 text-blue-700',
      'elutasítva': 'bg-red-100 text-red-700',
      'teljesítve': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  if (loading) {
    return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;
  }

  return (
    <div className="space-y-4" data-testid="feed-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800">Tápigénylések</h2>
        {!isAdmin && activeRental && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"
            data-testid="new-feed-request-btn"
          >
            <Plus size={16} /> Új igénylés
          </button>
        )}
      </div>

      {/* Info for students without rental */}
      {!isAdmin && !activeRental && (
        <Card className="bg-yellow-50 border-yellow-200">
          <p className="text-yellow-700 text-sm">
            ⚠️ Tápigényléshez aktív lóbérlés szükséges. Ha bérlésed van, de nem jelenik meg, keresd az edzőt!
          </p>
        </Card>
      )}

      {/* Feed Requests List */}
      {feedRequests.length === 0 ? (
        <Card className="text-center py-8">
          <span className="text-4xl mb-2">🌾</span>
          <p className="text-gray-500">Nincs tápigénylés</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {feedRequests.map((req) => (
            <Card key={req.id} data-testid={`feed-request-card-${req.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                  req.status === 'teljesítve' ? 'bg-green-100' : 
                  req.status === 'jóváhagyva' ? 'bg-blue-100' :
                  req.status === 'elutasítva' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {req.status === 'teljesítve' ? '✓' : 
                   req.status === 'jóváhagyva' ? '📦' :
                   req.status === 'elutasítva' ? '✗' : '🌾'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800">{req.feed_type}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(req.status)}`}>
                      {req.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Mennyiség: {req.quantity}</p>
                  {isAdmin && req.student && (
                    <p className="text-sm text-gray-500">
                      <User size={12} className="inline mr-1" />
                      {req.student.name} {req.horse && `• ${req.horse.name}`}
                    </p>
                  )}
                  {!isAdmin && req.horse && (
                    <p className="text-sm text-gray-500">Ló: {req.horse.name}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    <Calendar size={12} className="inline mr-1" />
                    {formatDate(req.created_at)}
                  </p>
                  {req.notes && <p className="text-xs text-gray-500 mt-1 italic">{req.notes}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  {isAdmin && req.status === 'függőben' && (
                    <button 
                      onClick={() => { setSelectedRequest(req); setShowApprovalModal(true); }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" 
                      title="Elbírálás"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  {isAdmin && req.status === 'jóváhagyva' && (
                    <button 
                      onClick={() => handleFulfill(req.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg" 
                      title="Teljesítve"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  {(isAdmin || (req.status === 'függőben' && req.student_id === user?.id)) && (
                    <button 
                      onClick={() => handleDelete(req.id)}
                      className="p-2 text-gray-400 hover:text-red-600" 
                      title="Törlés"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Request Modal (students only) */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Új tápigénylés</h3>
            {activeRental && (
              <p className="text-sm text-gray-600 mb-4 bg-purple-50 p-2 rounded-lg">
                🐴 Ló: <strong>{activeRental.horse_name || 'Bérelt ló'}</strong>
              </p>
            )}
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Táp típusa *</label>
                <select
                  value={form.feed_type}
                  onChange={(e) => setForm({...form, feed_type: e.target.value})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                >
                  <option value="">Válassz típust...</option>
                  {FEED_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Mennyiség *</label>
                <input
                  type="text"
                  value={form.quantity}
                  onChange={(e) => setForm({...form, quantity: e.target.value})}
                  placeholder="pl. 1 zsák, 5 kg"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megjegyzés</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  placeholder="Egyéb információk..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => setShowModal(false)} 
                className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600"
              >
                Mégse
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.feed_type || !form.quantity}
                className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Igénylés küldése
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Approval Modal (admin/trainer) */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Tápigénylés elbírálása</h3>
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="font-medium text-gray-800">{selectedRequest.feed_type}</p>
              <p className="text-sm text-gray-600">Mennyiség: {selectedRequest.quantity}</p>
              <p className="text-sm text-gray-500">Igénylő: {selectedRequest.student?.name || selectedRequest.student_name}</p>
              {selectedRequest.notes && <p className="text-sm text-gray-500 italic mt-1">{selectedRequest.notes}</p>}
            </div>
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setApprovalAction('approve')}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm ${
                    approvalAction === 'approve' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  ✓ Jóváhagyás
                </button>
                <button
                  onClick={() => setApprovalAction('reject')}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm ${
                    approvalAction === 'reject' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  ✗ Elutasítás
                </button>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Megjegyzés</label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder={approvalAction === 'reject' ? 'Elutasítás indoka...' : 'Megjegyzés...'}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => { setShowApprovalModal(false); setSelectedRequest(null); setApprovalAction(''); }}
                className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600"
              >
                Mégse
              </button>
              <button
                onClick={handleApproval}
                disabled={!approvalAction}
                className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Véglegesítés
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== PÉNZÜGYEK (KOMBINÁLT) PAGE ====================
const FinancesPage = ({ user, token }) => {
  const [activeTab, setActiveTab] = useState('payments');
  
  return (
    <div className="space-y-4" data-testid="finances-page">
      {/* Tab selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('payments')}
          className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
            activeTab === 'payments' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'
          }`}
        >
          💰 Fizetések
        </button>
        <button
          onClick={() => setActiveTab('rentals')}
          className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
            activeTab === 'rentals' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'
          }`}
        >
          📋 Bérlések
        </button>
        <button
          onClick={() => setActiveTab('feed')}
          className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 ${
            activeTab === 'feed' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border'
          }`}
        >
          🌾 Táp
        </button>
      </div>
      
      {activeTab === 'payments' && <PaymentsPage user={user} token={token} />}
      {activeTab === 'rentals' && <RentalsPage user={user} token={token} />}
      {activeTab === 'feed' && <FeedRequestsPage user={user} token={token} />}
    </div>
  );
};

// ==================== 16. FELADAT: DOKUMENTUMOK PAGE ====================
const DocumentsPage = ({ user, token, targetStudentId = null }) => {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [form, setForm] = useState({
    document_type: '',
    name: '',
    file_data: null,
    file_name: '',
    file_type: '',
    acceptances: []
  });
  
  const isAdmin = user?.role === 'admin' || user?.role === 'edzo';
  const studentId = targetStudentId || user?.id;

  const loadData = async () => {
    setLoading(true);
    try {
      const url = targetStudentId 
        ? `${API}/akademia/documents?token=${token}&student_id=${targetStudentId}`
        : `${API}/akademia/documents?token=${token}`;
      
      const [docsRes, typesRes, acceptRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/akademia/document-types`),
        axios.get(`${API}/akademia/enrollment-acceptances`)
      ]);
      setDocuments(docsRes.data);
      setDocumentTypes(typesRes.data);
      setAcceptances(acceptRes.data);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [token, targetStudentId]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete maximum 5MB lehet');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setForm({
        ...form,
        file_data: reader.result,
        file_name: file.name,
        file_type: file.type
      });
    };
    reader.readAsDataURL(file);
  };

  const handleAcceptanceToggle = (accId) => {
    const current = form.acceptances || [];
    if (current.includes(accId)) {
      setForm({ ...form, acceptances: current.filter(id => id !== accId) });
    } else {
      setForm({ ...form, acceptances: [...current, accId] });
    }
  };

  const handleUpload = async () => {
    try {
      const payload = {
        ...form,
        student_id: targetStudentId || undefined
      };
      await axios.post(`${API}/akademia/documents?token=${token}`, payload);
      setShowUploadModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleVerify = async (docId) => {
    try {
      await axios.put(`${API}/akademia/documents/${docId}/verify?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Biztosan törlöd ezt a dokumentumot?')) return;
    try {
      await axios.delete(`${API}/akademia/documents/${docId}?token=${token}`);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await axios.get(`${API}/akademia/documents/${doc.id}?token=${token}`);
      setSelectedDocument(res.data);
      setShowViewModal(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  const resetForm = () => {
    setForm({
      document_type: '',
      name: '',
      file_data: null,
      file_name: '',
      file_type: '',
      acceptances: []
    });
  };

  const getDocTypeName = (typeId) => {
    const docType = documentTypes.find(dt => dt.id === typeId);
    return docType?.name || typeId;
  };

  const requiresAcceptance = form.document_type === 'beiratkozasi_lap';

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  if (loading) {
    return <Card><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div></Card>;
  }

  return (
    <div className="space-y-4" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800">Dokumentumok</h2>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium text-sm"
          data-testid="upload-document-btn"
        >
          <Plus size={16} /> Feltöltés
        </button>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <Card className="text-center py-8">
          <span className="text-4xl mb-2">📄</span>
          <p className="text-gray-500">Nincs feltöltött dokumentum</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <Card key={doc.id} data-testid={`document-card-${doc.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                  doc.verified ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {doc.verified ? '✓' : '📄'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-800">{doc.name}</h3>
                    {doc.verified && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        Ellenőrzött
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{getDocTypeName(doc.document_type)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    <Calendar size={12} className="inline mr-1" />
                    Feltöltve: {formatDate(doc.uploaded_at)}
                  </p>
                  {doc.file_name && (
                    <p className="text-xs text-gray-500 mt-1">{doc.file_name}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button 
                    onClick={() => handleView(doc)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" 
                    title="Megtekintés"
                  >
                    <Eye size={16} />
                  </button>
                  {isAdmin && !doc.verified && (
                    <button 
                      onClick={() => handleVerify(doc.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg" 
                      title="Ellenőrzött jelölés"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  {(isAdmin || doc.student_id === user?.id) && (
                    <button 
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 text-gray-400 hover:text-red-600" 
                      title="Törlés"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Dokumentum feltöltése</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Dokumentum típusa *</label>
                <select
                  value={form.document_type}
                  onChange={(e) => setForm({...form, document_type: e.target.value, acceptances: []})}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                >
                  <option value="">Válassz típust...</option>
                  {documentTypes.map(dt => <option key={dt.id} value={dt.id}>{dt.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Dokumentum neve *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="pl. Beiratkozási lap 2026"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Fájl feltöltése</label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-gray-900 text-sm"
                />
                {form.file_name && (
                  <p className="text-xs text-green-600 mt-1">Kiválasztva: {form.file_name}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Max. 5MB, PDF/DOC/DOCX/JPG/PNG</p>
              </div>
              
              {/* Acceptances for enrollment documents */}
              {requiresAcceptance && (
                <div className="bg-yellow-50 p-3 rounded-xl">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    Beiratkozáshoz az alábbi szabályzatok elfogadása szükséges:
                  </p>
                  <div className="space-y-2">
                    {acceptances.map(acc => (
                      <label key={acc.id} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.acceptances.includes(acc.id)}
                          onChange={() => handleAcceptanceToggle(acc.id)}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{acc.name}</p>
                          <p className="text-xs text-gray-500">{acc.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => { setShowUploadModal(false); resetForm(); }}
                className="flex-1 py-2 border border-gray-300 rounded-xl text-gray-600"
              >
                Mégse
              </button>
              <button
                onClick={handleUpload}
                disabled={!form.document_type || !form.name || (requiresAcceptance && form.acceptances.length < acceptances.length)}
                className="flex-1 py-2 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Feltöltés
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">{selectedDocument.name}</h3>
              <button onClick={() => { setShowViewModal(false); setSelectedDocument(null); }}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Típus:</span>
                <span className="font-medium">{getDocTypeName(selectedDocument.document_type)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Feltöltve:</span>
                <span className="font-medium">{formatDate(selectedDocument.uploaded_at)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Feltöltő:</span>
                <span className="font-medium">{selectedDocument.uploaded_by_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Státusz:</span>
                <span className={`font-medium ${selectedDocument.verified ? 'text-green-600' : 'text-yellow-600'}`}>
                  {selectedDocument.verified ? 'Ellenőrzött' : 'Ellenőrzésre vár'}
                </span>
              </div>
              
              {selectedDocument.acceptances?.length > 0 && (
                <div className="bg-green-50 p-3 rounded-xl">
                  <p className="text-sm font-medium text-green-800 mb-1">Elfogadott szabályzatok:</p>
                  <ul className="text-xs text-green-700">
                    {selectedDocument.acceptances.map(accId => {
                      const acc = acceptances.find(a => a.id === accId);
                      return <li key={accId}>✓ {acc?.name || accId}</li>;
                    })}
                  </ul>
                </div>
              )}
              
              {selectedDocument.file_data && (
                <div className="mt-4">
                  {selectedDocument.file_type?.startsWith('image/') ? (
                    <img 
                      src={selectedDocument.file_data} 
                      alt={selectedDocument.name}
                      className="w-full rounded-xl"
                    />
                  ) : (
                    <a
                      href={selectedDocument.file_data}
                      download={selectedDocument.file_name}
                      className="flex items-center justify-center gap-2 py-3 bg-purple-100 text-purple-700 rounded-xl font-medium"
                    >
                      <Download size={16} /> Letöltés ({selectedDocument.file_name})
                    </a>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Admin oldal - Felhasználók kezelése
const AdminPage = ({ token, currentUser }) => {
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [activityData, setActivityData] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    identifier: '',
    name: '',
    role: 'akademista',
    programs: ['akademia'],
    training_direction: ''
  });
  const [pointsData, setPointsData] = useState({ points: 10, reason: '' });
  const [error, setError] = useState('');

  // Load users
  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/akademia/users?token=${token}`);
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setLoading(false);
    }
  };

  // Load deletion requests
  const loadDeletionRequests = async () => {
    try {
      const response = await axios.get(`${API}/akademia/deletion-requests?token=${token}&status=pending`);
      setDeletionRequests(response.data);
    } catch (err) {
      console.error('Failed to load deletion requests', err);
    }
  };

  // Load inactive users
  const loadInactiveUsers = async () => {
    try {
      const response = await axios.get(`${API}/akademia/users/inactive-warnings?token=${token}`);
      setInactiveUsers(response.data);
    } catch (err) {
      console.error('Failed to load inactive users', err);
    }
  };

  useEffect(() => {
    loadUsers();
    loadDeletionRequests();
    loadInactiveUsers();
  }, [token]);

  // Open modal for new user
  const openNewUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      identifier: '',
      name: '',
      role: 'akademista',
      programs: ['akademia'],
      training_direction: ''
    });
    setError('');
    setShowModal(true);
  };

  // Open modal for editing
  const openEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      identifier: user.identifier,
      name: user.name,
      role: user.role || 'akademista',
      programs: user.programs || ['akademia'],
      training_direction: user.training_direction || ''
    });
    setError('');
    setShowModal(true);
  };

  // Save user
  const saveUser = async () => {
    setError('');
    try {
      if (editingUser) {
        await axios.put(`${API}/akademia/users/${editingUser.id}?token=${token}`, formData);
      } else {
        await axios.post(`${API}/akademia/users?token=${token}`, formData);
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Open deletion request modal
  const openDeletionModal = (user) => {
    setSelectedUser(user);
    setDeletionReason('');
    setError('');
    setShowDeletionModal(true);
  };

  // Initiate deletion request
  const initiateDeletion = async () => {
    if (!deletionReason.trim()) {
      setError('Indoklás megadása kötelező');
      return;
    }
    try {
      await axios.post(`${API}/akademia/users/${selectedUser.id}/deletion-request?token=${token}`, {
        reason: deletionReason
      });
      setShowDeletionModal(false);
      loadUsers();
      loadDeletionRequests();
      alert('Törlési kérelem elküldve. A másik jogosult (admin vagy edző) jóváhagyása szükséges.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Approve deletion request
  const approveDeletion = async (requestId) => {
    try {
      const response = await axios.post(`${API}/akademia/deletion-requests/${requestId}/approve?token=${token}`);
      if (response.data.status === 'deleted') {
        alert('A felhasználó véglegesen törölve lett.');
      } else {
        alert('Jóváhagyás rögzítve. Várakozás a másik fél jóváhagyására.');
      }
      loadDeletionRequests();
      loadUsers();
      loadInactiveUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Reject deletion request
  const rejectDeletion = async (requestId) => {
    const reason = window.prompt('Elutasítás indoka (opcionális):');
    try {
      await axios.post(`${API}/akademia/deletion-requests/${requestId}/reject?token=${token}`, {
        reason: reason || ''
      });
      alert('Törlési kérelem elutasítva.');
      loadDeletionRequests();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // View user activity
  const viewActivity = async (user) => {
    try {
      const response = await axios.get(`${API}/akademia/users/${user.id}/activity?token=${token}`);
      setActivityData(response.data);
      setSelectedUser(user);
      setShowActivityModal(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Open points modal
  const openPointsModal = (user) => {
    setSelectedUser(user);
    setPointsData({ points: 10, reason: '' });
    setShowPointsModal(true);
  };

  // Add points
  const addPoints = async () => {
    try {
      const response = await axios.post(
        `${API}/akademia/users/${selectedUser.id}/points?token=${token}`,
        pointsData
      );
      if (response.data.new_badges?.length > 0) {
        alert(`🎉 Új jelvény szerzett: ${response.data.new_badges.map(b => b.name).join(', ')}`);
      }
      setShowPointsModal(false);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba történt');
    }
  };

  // Toggle program
  const toggleProgram = (program) => {
    const programs = formData.programs.includes(program)
      ? formData.programs.filter(p => p !== program)
      : [...formData.programs, program];
    setFormData({ ...formData, programs });
  };

  if (loading) {
    return <Card><p className="text-gray-500">Betöltés...</p></Card>;
  }

  const adminTabs = [
    { id: 'users', label: 'Felhasználók', icon: Users, count: users.length },
    { id: 'deletion', label: 'Törlési kérelmek', icon: FileText, count: deletionRequests.length },
    { id: 'inactive', label: 'Inaktív figyelmeztetések', icon: AlertTriangle, count: inactiveUsers.length },
  ];

  return (
    <div className="space-y-4" data-testid="admin-page">
      {/* Admin tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {adminTabs.map(tab => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveAdminTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                activeAdminTab === tab.id
                  ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <TabIcon size={16} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeAdminTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Users Tab */}
      {activeAdminTab === 'users' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Felhasználók kezelése</h2>
            <button
              onClick={openNewUser}
              className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-4 py-2 rounded-xl font-medium text-sm"
              data-testid="add-user-btn"
            >
              + Új felhasználó
            </button>
          </div>

          {/* User list */}
          <div className="space-y-2">
            {users.map(user => (
              <Card key={user.id} className="flex items-center gap-3" data-testid={`user-card-${user.id}`}>
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                  {user.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-800 truncate">{user.name}</p>
                    <span className="text-purple-600 font-bold text-sm">{user.points || 0} pt</span>
                  </div>
                  <p className="text-sm text-gray-500">@{user.username}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {user.badges?.map(b => (
                      <span key={b.id} title={b.name}>{b.icon}</span>
                    ))}
                    {user.programs?.map(p => (
                      <span key={p} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {p === 'akademia' ? 'Akadémia' : 'Apród'}
                      </span>
                    ))}
                    {user.role === 'admin' && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Admin</span>
                    )}
                    {user.role === 'edzo' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Edző</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => viewActivity(user)}
                    className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"
                    title="Aktivitás megtekintése"
                    data-testid={`view-activity-${user.id}`}
                  >
                    <Activity size={16} />
                  </button>
                  <button
                    onClick={() => openPointsModal(user)}
                    className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg text-sm"
                    title="Pont hozzáadása"
                  >
                    +🏆
                  </button>
                  <button
                    onClick={() => openEditUser(user)}
                    className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"
                  >
                    <Edit2 size={16} />
                  </button>
                  {user.username !== 'gadmin' && (
                    <button
                      onClick={() => openDeletionModal(user)}
                      className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                      title="Törlés kezdeményezése"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Deletion Requests Tab */}
      {activeAdminTab === 'deletion' && (
        <>
          <h2 className="text-xl font-bold text-gray-800">Függőben lévő törlési kérelmek</h2>
          {deletionRequests.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Nincsenek függőben lévő törlési kérelmek</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {deletionRequests.map(req => (
                <Card key={req.id} className="border-l-4 border-l-yellow-500" data-testid={`deletion-request-${req.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{req.user_name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Indoklás:</strong> {req.reason}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Kezdeményezte: {req.initiated_by_name} ({req.initiated_by_role === 'admin' ? 'Admin' : 'Edző'})
                      </p>
                      <div className="flex gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${req.admin_approval ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {req.admin_approval ? '✓ Admin jóváhagyta' : '○ Admin jóváhagyás várakozik'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${req.coach_approval ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {req.coach_approval ? '✓ Edző jóváhagyta' : '○ Edző jóváhagyás várakozik'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {((currentUser?.role === 'admin' && !req.admin_approval) || 
                        (currentUser?.role === 'edzo' && !req.coach_approval)) && (
                        <>
                          <button
                            onClick={() => approveDeletion(req.id)}
                            className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-xl text-sm font-medium"
                            data-testid={`approve-deletion-${req.id}`}
                          >
                            <Check size={14} />
                            Jóváhagyás
                          </button>
                          <button
                            onClick={() => rejectDeletion(req.id)}
                            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-xl text-sm font-medium"
                            data-testid={`reject-deletion-${req.id}`}
                          >
                            <XCircle size={14} />
                            Elutasítás
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Inactive Users Tab */}
      {activeAdminTab === 'inactive' && (
        <>
          <h2 className="text-xl font-bold text-gray-800">Inaktív felhasználók (6+ hónap)</h2>
          {inactiveUsers.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <Check size={48} className="mx-auto text-green-300 mb-3" />
                <p className="text-gray-500">Nincsenek 6 hónapnál régebben inaktív felhasználók</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {inactiveUsers.map(item => (
                <Card key={item.user.id} className="border-l-4 border-l-red-500" data-testid={`inactive-user-${item.user.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-800">{item.user.name}</h3>
                        <AlertTriangle size={16} className="text-red-500" />
                      </div>
                      <p className="text-sm text-gray-600">@{item.user.username}</p>
                      <p className="text-sm text-red-600 mt-1">
                        <strong>{item.consecutive_inactive_months} egymást követő hónap</strong> inaktivitás
                      </p>
                      <p className="text-xs text-red-500 mt-1">
                        A törlés feltételei teljesültek (6+ hónap folyamatos távolmaradás)
                      </p>
                      {item.has_pending_deletion && (
                        <p className="text-xs text-yellow-600 mt-2">
                          ⏳ Törlési kérelem már folyamatban
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewActivity(item.user)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-xl text-sm font-medium"
                      >
                        Aktivitás
                      </button>
                      {!item.has_pending_deletion && (
                        <button
                          onClick={() => openDeletionModal(item.user)}
                          className="px-3 py-2 bg-red-500 text-white rounded-xl text-sm font-medium"
                        >
                          Törlés kezdeményezése
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {editingUser ? 'Felhasználó szerkesztése' : 'Új felhasználó'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Név</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="Teljes név"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Felhasználónév</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="felhasznalonev"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Azonosító (10 számjegy)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900 tracking-wider"
                  placeholder="1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szerepkör</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                >
                  <option value="akademista">Akadémista</option>
                  <option value="edzo">Edző</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Programok</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => toggleProgram('akademia')}
                    className={`px-4 py-2 rounded-xl border ${formData.programs.includes('akademia') ? 'bg-purple-100 border-purple-500 text-purple-700' : 'border-gray-300 text-gray-600'}`}
                  >
                    Akadémia
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleProgram('aprod')}
                    className={`px-4 py-2 rounded-xl border ${formData.programs.includes('aprod') ? 'bg-blue-100 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600'}`}
                  >
                    Lovas Apród
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Képzési irány</label>
                <input
                  type="text"
                  value={formData.training_direction}
                  onChange={(e) => setFormData({ ...formData, training_direction: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="pl. Díjugratás, Díjlovaglás"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                >
                  Mégse
                </button>
                <button
                  onClick={saveUser}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium"
                >
                  Mentés
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Points Modal */}
      {showPointsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Pont hozzáadása: {selectedUser.name}
            </h3>
            <p className="text-sm text-gray-500 mb-4">Jelenlegi: {selectedUser.points || 0} pont</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pont (+/-)</label>
                <input
                  type="number"
                  value={pointsData.points}
                  onChange={(e) => setPointsData({ ...pointsData, points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                />
                <div className="flex gap-2 mt-2">
                  {[10, 25, 50, 100].map(p => (
                    <button
                      key={p}
                      onClick={() => setPointsData({ ...pointsData, points: p })}
                      className="px-3 py-1 rounded-lg bg-purple-100 text-purple-700 text-sm"
                    >
                      +{p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indoklás</label>
                <input
                  type="text"
                  value={pointsData.reason}
                  onChange={(e) => setPointsData({ ...pointsData, reason: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-purple-500 outline-none text-gray-900"
                  placeholder="pl. Edzésen való részvétel"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowPointsModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                >
                  Mégse
                </button>
                <button
                  onClick={addPoints}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-medium"
                >
                  Hozzáadás
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Deletion Request Modal */}
      {showDeletionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="deletion-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Törlés kezdeményezése</h3>
                <p className="text-sm text-gray-500">{selectedUser.name}</p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Figyelem!</strong> A törléshez admin ÉS edző jóváhagyása is szükséges.
                A végleges törlés csak mindkét fél beleegyezése után történik meg.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Törlés indoklása <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 focus:border-red-500 outline-none text-gray-900 min-h-[100px] resize-none"
                  placeholder="Kérjük, adja meg a törlés okát..."
                  data-testid="deletion-reason-input"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowDeletionModal(false)}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 font-medium text-gray-700"
                >
                  Mégse
                </button>
                <button
                  onClick={initiateDeletion}
                  className="flex-1 py-3 px-4 rounded-xl bg-red-500 text-white font-medium"
                  data-testid="confirm-deletion-btn"
                >
                  Törlés kezdeményezése
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Activity Modal */}
      {showActivityModal && activityData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="activity-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Activity size={24} className="text-blue-600" />
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Aktivitás áttekintés</h3>
                  <p className="text-sm text-gray-500">{activityData.user_name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowActivityModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {/* Warning banner if applicable */}
            {activityData.deletion_warning && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle size={18} />
                  <strong>Törlési figyelmeztetés!</strong>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  {activityData.deletion_warning_message}
                </p>
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-purple-50 rounded-xl p-3">
                <p className="text-xs text-purple-600 font-medium">Jelenlegi pont</p>
                <p className="text-2xl font-bold text-purple-700">{activityData.current_points}</p>
              </div>
              <div className={`rounded-xl p-3 ${activityData.consecutive_inactive_months >= 6 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <p className={`text-xs font-medium ${activityData.consecutive_inactive_months >= 6 ? 'text-red-600' : 'text-gray-600'}`}>
                  Egymást követő inaktív hónap
                </p>
                <p className={`text-2xl font-bold ${activityData.consecutive_inactive_months >= 6 ? 'text-red-700' : 'text-gray-700'}`}>
                  {activityData.consecutive_inactive_months}
                </p>
              </div>
            </div>

            {/* Monthly breakdown */}
            <h4 className="font-semibold text-gray-700 mb-2">Havi megjelenések (utolsó 12 hónap)</h4>
            <div className="space-y-2">
              {activityData.months?.map((month, index) => (
                <div key={month.month} className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-20">{month.month}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        month.appearances >= month.required ? 'bg-green-500' : 
                        month.appearances > 0 ? 'bg-yellow-500' : 'bg-red-300'
                      }`}
                      style={{ width: `${Math.min(100, (month.appearances / month.required) * 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium w-16 text-right ${
                    month.appearances >= month.required ? 'text-green-600' : 
                    month.appearances > 0 ? 'text-yellow-600' : 'text-red-500'
                  }`}>
                    {month.appearances}/{month.required}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500">
                * Elvárt minimum: havi 4 megjelenés. 6 egymást követő inaktív hónap után a törlés feltételei teljesülnek.
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Fő Akadémia komponens
export default function Akademia() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  // Bejelentkezés ellenőrzése
  useEffect(() => {
    const savedUser = localStorage.getItem('akademia_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('akademia_user');
      }
    }
    setLoading(false);
  }, []);

  // Unread chat count polling
  useEffect(() => {
    if (!user) return;
    
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('akademia_token');
        const response = await axios.get(`${API}/akademia/chat/unread?token=${token}`);
        setUnreadChatCount(response.data.unread_count || 0);
      } catch (err) {
        console.error('Failed to fetch unread count', err);
      }
    };
    
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [user]);

  // Bejelentkezés
  const handleLogin = async (username, identifier) => {
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/akademia/login`, {
        username,
        identifier
      });
      
      const { token, user } = response.data;
      localStorage.setItem('akademia_token', token);
      localStorage.setItem('akademia_user', JSON.stringify(user));
      setUser(user);
    } catch (error) {
      const message = error.response?.data?.detail || 'Hibás felhasználónév vagy azonosító';
      setLoginError(message);
    }
  };

  // Kilépés
  const handleLogout = async () => {
    const token = localStorage.getItem('akademia_token');
    if (token) {
      try {
        await axios.post(`${API}/akademia/logout?token=${token}`);
      } catch (e) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('akademia_token');
    localStorage.removeItem('akademia_user');
    setUser(null);
    setActiveTab('home');
  };

  // Betöltés
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center">
        <div className="text-white text-xl">Betöltés...</div>
      </div>
    );
  }

  // Bejelentkezési képernyő
  if (!user) {
    return <LoginScreen onLogin={handleLogin} error={loginError} />;
  }

  // Fő alkalmazás
  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-4">
      <AkademiaHeader user={user} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto px-4 py-4">
        {activeTab === 'home' && <HomePage user={user} token={localStorage.getItem('akademia_token')} />}
        {activeTab === 'workouts' && (
          <TrainingsPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'plan' && (
          <TrainingPlanPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'badges' && (
          <PointsAndBadgesPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'board' && (
          <MessageBoardPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'documents' && (
          <DocumentsPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'chat' && (
          <ChatPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'horses' && (user?.role === 'admin' || user?.role === 'edzo') && (
          <HorsesAndBoxesPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'finances' && (user?.role === 'admin' || user?.role === 'edzo') && (
          <FinancesPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'rentals' && (user?.role === 'admin' || user?.role === 'edzo') && (
          <RentalsPage user={user} token={localStorage.getItem('akademia_token')} />
        )}
        {activeTab === 'settings' && (
          <Card>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Profil</h2>
            <p className="text-gray-500">Hamarosan elérhető...</p>
          </Card>
        )}
        {activeTab === 'admin' && (user?.role === 'admin' || user?.role === 'edzo') && (
          <AdminPage token={localStorage.getItem('akademia_token')} currentUser={user} />
        )}
      </main>

      <MobileNav 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        isAdmin={user?.role === 'admin' || user?.role === 'edzo'} 
        unreadChatCount={unreadChatCount}
      />
    </div>
  );
}

// Exportált komponensek újrafelhasználáshoz
export { Card, BigButton, StatusBadge, STATUS_COLORS, AkademiaHeader, MobileNav };
